/**
 * The two dictionaries. Keys are in English and name the *meaning*, not the
 * text (doc 80 §1.3): `content.publish.confirm`, never `sei_sicuro`. Italian
 * is the reference language — write it first — and English is the fallback.
 * No component may hardcode a user-facing string: everything goes through
 * `t()` (see `./t.ts`) against one of these two trees. `dict.test.ts` proves
 * the two trees have exactly the same keys.
 */

import { libraryEn, libraryIt } from './dict-library.js'
import { accessEn, accessIt } from './dict-access.js'
import { layoutsEn, layoutsIt } from './dict-layouts.js'
import { editorialEn, editorialIt } from './dict-editorial.js'
import { aiGenEn, aiGenIt } from './dict-ai.js'
import { inPlaceEn, inPlaceIt } from './dict-inplace.js'
import { themeDesignEn, themeDesignIt } from './dict-theme-design.js'
import { polishEn, polishIt } from './dict-polish.js'
import { mainImageEn, mainImageIt } from './dict-main-image.js'
import { contentTreeEn, contentTreeIt } from './dict-content-tree.js'
import { shellEn, shellIt } from './dict-shell.js'
import { sectionTreeEn, sectionTreeIt } from './dict-section-tree.js'
import { editorEn, editorIt } from './dict-editor.js'

export interface DictNode {
  [key: string]: string | DictNode
}

export const it: DictNode = {
  app: {
    title: 'CMS',
  },
  auth: {
    title: 'Accedi al CMS',
    description: 'Il cervello dei contenuti di Sviluppatore Migliore.',
    submit: 'Continua con Security',
    checking: 'Controllo la sessione…',
    invalidTitle: 'Accesso non riuscito',
    networkTitle: 'Impossibile contattare l\'API',
    logout: 'Esci',
    signedInAs: 'Connesso con l’accesso unico',
    errors: {
      generic: 'L’accesso non è andato a buon fine. Riprova; se succede ancora, avvisa chi amministra il sistema.',
      login_required: 'La sessione su Security non c’è più: entra di nuovo.',
      session_expired: 'Hai impiegato troppo tempo: la richiesta di accesso scade dopo dieci minuti. Ricomincia.',
      state_mismatch: 'La risposta non corrisponde alla richiesta di accesso partita da qui: per sicurezza è stata rifiutata. Riprova da questa pagina.',
      issuer_mismatch: 'La risposta arriva da un indirizzo diverso da quello dell’accesso unico: è stata rifiutata.',
      access_denied: 'L’accesso è stato negato su Security.',
      sso_not_configured: 'L’accesso unico non è ancora configurato su questo ambiente. Serve chi amministra il sistema.',
      sso_unavailable: 'Security non risponde in questo momento. Riprova fra poco.',
      sso_token_exchange_failed: 'Security non ha accettato lo scambio finale dell’accesso. Riprova; se insiste, avvisa chi amministra il sistema.',
      interaction_required: 'Security ha bisogno che confermi tu: premi il bottone e prosegui lì.',
      consent_required: 'La prima volta Security chiede il consenso per il CMS: premi il bottone e dai l’ok sulla sua pagina.',
      account_selection_required: 'Security vuole sapere con quale account entri: premi il bottone e scegli.',
      unauthorized: 'Security ha risposto con un’identità che il CMS non può accettare: manca il cliente sul tuo account, oppure il token non è valido. Avvisa chi amministra il sistema.',
      tenant_not_provisioned: 'La tua organizzazione su Security non è ancora collegata a un cliente del CMS: finché il collegamento non c’è, non c’è nulla da mostrare. Avvisa chi amministra il sistema.',
      exchange_failed: 'Lo scambio finale con Security non è riuscito. Riprova; se insiste, avvisa chi amministra il sistema.',
      missing_code: 'Security è tornata senza il codice di accesso. Riprova.',
      missing_id_token: 'Security non ha restituito l’identità insieme al token. Avvisa chi amministra il sistema.',
      jwks_unavailable: 'Non riesco a leggere le chiavi di firma di Security. Riprova fra poco.',
      code: 'Codice tecnico: {{code}}',
    },
    returnHint: 'Dopo l’accesso torni alla pagina che stavi aprendo.',
    footnote: 'L’accesso passa da Security, il sistema di accesso unico di Sviluppatore Migliore.',
  },
  nav: {
    overview: 'Panoramica',
    contents: 'Contenuti',
    knowledge: 'Conoscenza',
    people: 'Persone',
    apiKeys: 'Chiavi API',
    operator: 'Operatore',
    home: 'Tutti i siti',
    pages: 'Pagine',
    menus: 'Menu',
    listings: 'Elenchi',
    media: 'Media',
    schedule: 'Calendario',
    redirects: 'Redirect',
    theme: 'Tema',
    sections: 'Sezioni',
    publications: 'Pubblicazioni',
    settings: 'Impostazioni',
  },
  workspace: {
    site: 'Sito attivo',
    work: 'Lavoro',
    system: 'Sistema',
    live: 'Sito pubblicato',
    notLive: 'Non ancora pubblicato',
    openMenu: 'Apri il menu',
    closeMenu: 'Chiudi il menu',
    toggleTheme: 'Cambia tema',
    language: 'Lingua interfaccia',
    user: 'Utente',
  },
  dashboard: {
    title: 'Panoramica',
    subtitle: 'Stato editoriale e lavoro recente del sito selezionato.',
    overview: 'Stato dei contenuti',
    activeContent: 'Contenuti attivi',
    activeExplanation: 'bozze, revisioni e contenuti pubblicati',
    openAll: 'Apri tutti',
    recent: 'Modificati di recente',
    recentHelp: 'Gli ultimi contenuti restituiti dal CMS per questo sito.',
    error: 'Non è stato possibile caricare la panoramica',
    noSite: 'Nessun sito disponibile',
  },
  status: {
    draft: 'Bozza',
    review: 'In revisione',
    published: 'Pubblicato',
    trashed: 'Cestinato',
    draftPlural: 'Bozze',
    reviewPlural: 'In revisione',
    publishedPlural: 'Pubblicati',
  },
  list: {
    title: 'Contenuti',
    subtitle: 'Cerca, filtra e apri i contenuti del sito selezionato.',
    resultCount: 'contenuti trovati',
    filters: {
      site: 'Sito',
      allSites: 'Tutti i siti',
      type: 'Tipo',
      allTypes: 'Tutti i tipi',
      status: 'Stato',
      allStatuses: 'Tutti gli stati',
    },
    search: {
      placeholder: 'Cerca per titolo…',
      label: 'Cerca per titolo',
    },
    columns: {
      title: 'Titolo',
      type: 'Tipo',
      status: 'Stato',
      updatedAt: 'Aggiornato',
      author: 'Autore',
      authorUnavailable: 'Non disponibile: l\'elenco dei contenuti non porta ancora chi ha scritto l\'ultima modifica. Vedi la storia del singolo contenuto.',
      locale: 'Lingua',
    },
    empty: 'Nessun contenuto trovato con questi filtri.',
    loading: 'Caricamento…',
    errorTitle: 'Non è stato possibile caricare i contenuti',
    filtersErrorTitle: 'Siti e tipi di contenuto non sono disponibili',
    newContent: 'Nuovo contenuto',
    openHistory: 'Storia',
    publishSite: 'Pubblica sito',
    pagination: {
      previous: 'Precedente',
      next: 'Successiva',
      pageInfo: '{{from}}–{{to}} di {{total}}',
    },
    subtitleFor: 'Tutti i contenuti di {{site}}',
    actions: {
      open: 'Apri',
      label: 'Azioni',
      for: 'Azioni per «{{title}}»',
    },
    emptyTitle: 'Nessun contenuto',
    emptyFiltered: 'Nessun contenuto corrisponde ai filtri',
    emptyFilteredHelp: 'Prova a togliere qualche filtro o a cambiare la ricerca.',
    clearFilters: 'Togli i filtri',
    emptyHelp: 'Crea il primo contenuto di questo sito.',
  },
  edit: {
    titleFor: 'Modifica: {{title}}',
    newTitle: 'Nuovo contenuto',
    reasonLabel: 'Motivo della modifica',
    reasonPlaceholder: 'Perché stai facendo questa modifica? Resta nella cronologia.',
    reasonRequired: 'Il motivo è obbligatorio: finisce nella cronologia e rende leggibile il lavoro di tutti.',
    requiredFields: 'Scegli sito, tipo e lingua; inserisci percorso e motivo della modifica.',
    save: 'Salva',
    saving: 'Salvataggio…',
    saved: 'Salvato',
    unsaved: 'Hai modifiche non salvate. Salvale prima di cambiare stato, usare l’AI o cestinare il contenuto.',
    unsavedShort: 'Modifiche non salvate',
    noChanges: 'Nessuna modifica',
    details: 'Dettagli',
    versionLabel: 'Versione',
    workflow: 'Stato editoriale',
    contentHelp: 'Campi editoriali e dati strutturati del contenuto.',
    seoHelp: 'Metadati, dati per i motori di ricerca e risposte generative.',
    noSeoFields: 'Questo tipo non dichiara campi SEO separati.',
    leaveTitle: 'Modifiche non salvate',
    leaveMessage: 'Uscendo da questa pagina perderai le modifiche non salvate.',
    leaveConfirm: 'Esci senza salvare',
    tabs: {
      content: 'Contenuto',
      seo: 'SEO',
      tools: 'Strumenti',
    },
    cancel: 'Annulla',
    slug: 'Percorso (slug)',
    locale: 'Lingua',
    version: 'Versione {{version}}',
    otherFieldsTitle: 'Altro',
    otherFieldsHelp: 'Campi presenti nel contenuto ma non dichiarati dal tipo. Restano modificabili: non si perdono mai.',
    protectedField: 'Campo protetto: non modificabile da qui.',
    protectedFieldReason: 'I campi protetti si cambiano solo con l\'operazione dedicata (per esempio il cambio di slug).',
    unknownFieldType: 'Tipo di campo "{{type}}" non ancora previsto: modificabile come JSON grezzo.',
    trash: 'Cestina',
    restore: 'Ripristina',
    changeStatus: 'Cambia stato',
    confirmTrash: 'Spostare questo contenuto nel cestino?',
    loadError: 'Non è stato possibile caricare questo contenuto.',
    backToList: 'Torna all\'elenco',
    savedPublishedHint: 'Salvato come pubblicato. Per portarlo online premi «Pubblica sito».',
    readOnlyRole: 'Il tuo ruolo permette solo la lettura di questo contenuto.',
    unknownType: 'Il tipo di contenuto «{{type}}» non è più definito: i campi sono mostrati in forma tecnica.',
    inTrash: 'Questo contenuto è nel cestino',
    inTrashHelp: 'Non compare sul sito. Ripristinalo per modificarlo di nuovo.',
    savedNotOnlineTitle: 'Salvato, ma non ancora online',
    savedNotOnline: 'Le modifiche sono nel CMS. Il sito pubblico si aggiorna quando premi «Pubblica sito».',
    tabsLabel: 'Sezioni dell’editor',
    noDeclaredFields: 'Questo tipo di contenuto non dichiara campi modificabili.',
    searchPreview: 'Anteprima nei risultati di ricerca',
    noDescription: 'Nessuna descrizione: i motori di ricerca ne sceglieranno una dal testo.',
    statusPending: 'Al salvataggio lo stato diventerà «{{status}}»',
    statusHelp: {
      draft: 'Bozza: visibile solo nel CMS.',
      review: 'In revisione: pronto per essere controllato.',
      published: 'Pubblicato: andrà online con la prossima pubblicazione del sito.',
      trashed: 'Nel cestino: non compare sul sito.',
    },
    address: 'Indirizzo',
    publicAddress: 'Indirizzo pubblico',
    type: 'Tipo',
    publishedAt: 'Pubblicato il',
    writtenWith: 'Scritto con l’assistente',
    reviewedWith: 'Rivisto con l’assistente',
    saveBar: 'Barra di salvataggio',
    discard: 'Annulla modifiche',
    saveAndMarkPublished: 'Salva e segna come pubblicato',
    trashed: 'Contenuto spostato nel cestino.',
    restored: 'Contenuto ripristinato.',
    cannotCreate: 'Il tuo ruolo non permette di creare contenuti in questo sito.',
    newSubtitle: 'Scegli il tipo, dai un titolo e un indirizzo: il resto lo completi nell’editor.',
    titleLabel: 'Titolo',
    titleHelp: 'Serve a proporre l’indirizzo. Lo scriverai nei campi del contenuto.',
    titleNotStored: 'Il titolo non viene salvato da solo: dopo la creazione compilalo nei campi.',
    addressPreview: 'Indirizzo: {{address}}',
    slugHelp: 'Solo lettere minuscole, numeri e trattini.',
    creating: 'Creazione…',
    create: 'Crea contenuto',
    technicalPath: 'Percorso tecnico: {{path}}',
  },
  page: {
    titleFor: 'In pagina: {{title}}',
    openForm: 'Apri nel modulo',
    openInPage: 'Modifica in pagina',
    frameTitle: 'La pagina, modificabile dove la leggi',
    loadingPage: 'Carico la pagina…',
    howItWorks: 'Questa è la pagina vera, resa dal CMS. I riquadri arancioni sono i campi: cliccane uno e scrivi. In questa pagina ce ne sono {{fields}}. Le modifiche si salvano da qui sotto, con un motivo.',
    readOnly: 'Puoi leggere la pagina ma non modificarla: il tuo ruolo non ha il permesso di scrittura.',
    pending: 'Modifiche in sospeso: {{count}}.',
    noPending: 'Nessuna modifica in sospeso.',
    discard: 'Scarta le modifiche',
    linkBlocked: 'Il link a {{href}} non si segue da qui: porterebbe via la pagina che stai modificando, con dentro le modifiche non salvate.',
    preview: 'Anteprima',
    eyebrow: 'Composizione della pagina',
  },
  fields: {
    string: 'Testo',
    text: 'Testo lungo',
    richtext: 'Testo ricco',
    number: 'Numero',
    boolean: 'Booleano',
    enum: 'Elenco chiuso',
    date: 'Data',
    media: 'Media',
    reference: 'Riferimento',
    list: 'Elenco di oggetti',
    json: 'JSON',
    addItem: 'Aggiungi elemento',
    removeItem: 'Rimuovi',
    moveUp: 'Sposta su',
    moveDown: 'Sposta giù',
    jsonInvalid: 'JSON non valido: la modifica non è stata applicata.',
    referenceHelp: 'Riferimento a un contenuto di tipo "{{to}}". Incolla l\'identificativo.',
    booleanTrue: 'Sì',
    booleanFalse: 'No',
    richtextHelp: 'Testo formattato in Markdown.',
    mediaHelp: 'Indirizzo dell’immagine o del file.',
    jsonHelp: 'Valore strutturato in formato JSON.',
    itemNumber: 'Elemento {{number}}',
  },
  conflict: {
    title: 'Qualcun altro ha modificato questo contenuto',
    description: 'Nel frattempo il documento è cambiato (ora è alla versione {{actualVersion}}, la tua modifica partiva dalla {{expectedVersion}}). Quello che avevi scritto non è andato perso: scegli cosa tenere.',
    mine: 'La tua versione',
    theirs: 'La versione sul server',
    keepMine: 'Tieni la mia e riprova',
    keepTheirs: 'Ricarica quella del server',
    dismiss: 'Chiudi',
  },
  history: {
    title: 'Storia del contenuto',
    who: 'Chi',
    when: 'Quando',
    ip: 'Indirizzo IP',
    reason: 'Motivo',
    event: 'Evento',
    viewAt: 'Vedi a questa versione',
    viewingAt: 'Stai vedendo il documento come era alla versione {{version}}.',
    backToCurrent: 'Torna alla versione corrente',
    back: 'Torna al contenuto',
    empty: 'Nessun evento registrato.',
    loading: 'Caricamento della cronologia…',
    errorTitle: 'Non è stato possibile caricare la cronologia',
    subtitle: 'Ogni modifica, con chi l’ha fatta e perché.',
    versionNumber: 'Versione {{version}}',
    person: 'Persona',
  },
  publish: {
    title: 'Pubblicazione',
    confirmMessage: 'Pubblicare ora il sito? Il contenuto pubblicato sarà visibile pubblicamente.',
    confirm: 'Pubblica',
    cancel: 'Annulla',
    inProgress: 'Pubblicazione in corso…',
    progress: '{{completed}} di {{total}} pagine preparate',
    phase: {
      planning: 'Controllo dei contenuti da pubblicare…',
      rendering: 'Generazione delle pagine statiche…',
      deploying: 'Distribuzione sulla rete Cloudflare…',
      completed: 'Distribuzione completata.',
      failed: 'Pubblicazione interrotta.',
    },
    success: 'Sito pubblicato.',
    openDeployment: 'Apri il sito distribuito',
    failure: 'La pubblicazione non è riuscita.',
    warnings: 'Avvisi di questa pubblicazione: {{count}}',
    checkStatus: 'Controlla stato',
    confirmMessageFor: 'Tutte le modifiche salvate di {{site}} andranno online. Ci vuole circa un minuto.',
    successHelp: 'Il sito pubblico è aggiornato.',
  },
  ai: {
    title: 'Scrittura e revisione con AI',
    description: 'Scegli esplicitamente l’abbonamento e il campo. Il CMS usa la conoscenza aziendale del cliente e registra quale account ha lavorato.',
    write: 'Scrittura',
    review: 'Revisione',
    chooseAccount: 'Scegli un abbonamento',
    chooseField: 'Scegli il campo',
    prompt: 'Descrivi cosa deve scrivere o rivedere…',
    generate: 'Avvia',
    working: 'Sto lavorando…',
    purpose: 'Cosa vuoi fare',
    account: 'Account AI',
    field: 'Campo',
    promptLabel: 'Istruzioni per l’assistente',
    promptHelp: 'Descrivi cosa vuoi ottenere. La conoscenza del cliente viene aggiunta in automatico.',
    done: 'Testo inserito nel campo: controllalo e salva.',
  },
  release: {
    notes: 'Note di rilascio',
    updated: 'Aggiornato alla versione {{version}}',
    dismiss: 'Ho capito',
    notesHelp: 'Le novità di Migliore CMS, dalla più recente.',
  },
  common: {
    add: 'Aggiungi',
    cancel: 'Annulla',
    loading: 'Caricamento…',
    remove: 'Rimuovi',
    save: 'Salva',
    saved: 'Salvato',
    saving: 'Salvataggio…',
    search: 'Cerca',
    unsaved: 'Modifiche non salvate',
    close: 'Chiudi',
    more: 'Altre azioni',
  },
  guard: {
    title: 'Modifiche non salvate',
    message: 'Uscendo perderai le modifiche non salvate.',
    leave: 'Esci senza salvare',
    stay: 'Resta qui',
  },
  site: {
    area: 'Sito',
    navigation: 'Gestione del sito',
    settings: 'Configurazione',
    settingsIntro: 'Identità, domini, lingue e destinazione tecnica del sito selezionato.',
    menu: 'Menu',
    theme: 'Tema',
    sections: 'Sezioni',
    identity: 'Identità del sito',
    name: 'Nome',
    primaryDomain: 'Dominio principale',
    status: 'Stato',
    project: 'Progetto',
    noTheme: 'Nessun tema attivo',
    locales: 'Lingue',
    domains: 'Domini',
    primary: 'Principale',
    alias: 'Alias',
    addDomain: 'Collega un dominio',
    domainIntro: 'Aggiungi un alias oppure cambia il dominio principale.',
    domain: 'Dominio',
    makePrimary: 'Imposta come dominio principale',
    domainSaved: 'Dominio aggiornato.',
    repository: 'Repository di pubblicazione',
    repositoryIntro: 'Repository e ramo isolato usati per gli asset e la pubblicazione del sito.',
    repositoryUrl: 'URL del repository',
    branch: 'Ramo CMS',
    repositorySaved: 'Repository aggiornato.',
    languages: 'Lingue del sito',
    languagesIntro: 'Definisci le lingue disponibili e quella predefinita. Nessuna lingua viene presunta.',
    defaultLocale: 'Lingua predefinita',
    localesInvalid: 'La lingua predefinita deve essere compresa nell’elenco.',
    localesSaved: 'Lingue aggiornate.',
    delivery: 'Destinazione di pubblicazione',
    deliveryIntro: 'Abilita la destinazione Cloudflare Pages predisposta dall’operatore della piattaforma.',
    deliveryProvider: 'Provider',
    deliveryProject: 'Progetto autorizzato',
    deliveryAuthorization: 'Autorizzazione',
    deliveryAuthorized: 'Autorizzata',
    deliveryUnauthorized: 'Mancante',
    deliveryNotBound: 'Non assegnato',
    deliveryToggle: 'Abilita le pubblicazioni verso questa destinazione',
    deliveryOperatorRequired: 'La destinazione non è ancora autorizzata. Chiedi all’operatore della piattaforma di collegare questo sito: nome del progetto e account non possono essere scelti qui.',
    deliveryEnabled: 'Destinazione di pubblicazione abilitata.',
    deliveryDisabled: 'Destinazione di pubblicazione disabilitata.',
    lastPublished: 'Pubblicato {{when}}',
    neverPublished: 'Mai pubblicato',
    notFound: 'Sito non trovato',
    notFoundHelp: 'Il sito non esiste oppure non hai i permessi per vederlo.',
    readOnly: 'Puoi consultare queste impostazioni ma non modificarle.',
    addDomainAction: 'Aggiungi dominio',
    openTheme: 'Apri il tema',
    publications: 'Pubblicazioni',
    publicationCount: '{{count}} pubblicazioni',
  },
  menu: {
    intro: 'Costruisci navigazioni ordinate, annidate e collegate ai contenuti reali.',
    menus: 'Menu del sito',
    items: 'voci',
    new: 'Nuovo menu',
    key: 'Chiave',
    addItem: 'Aggiungi voce',
    label: 'Etichetta',
    targetType: 'Destinazione',
    reference: 'Contenuto',
    external: 'Indirizzo esterno',
    query: 'Raccolta dinamica',
    content: 'Contenuto collegato',
    url: 'Indirizzo',
    contentType: 'Tipo di contenuto',
    limit: 'Limite',
    visibility: 'Visibilità',
    always: 'Sempre',
    style: 'Aspetto',
    normal: 'Normale',
    featured: 'In evidenza',
    addChild: 'Aggiungi sottovoce',
    moveUp: 'Sposta sopra',
    moveDown: 'Sposta sotto',
    empty: 'Il menu è vuoto',
    emptyHelp: 'Aggiungi la prima voce e scegli una destinazione reale.',
    saved: 'Menu salvato.',
    untitled: 'Voce senza nome',
    labelIn: 'Etichetta ({{locale}})',
    desktop: 'Desktop',
    mobile: 'Mobile',
    callToAction: 'Pulsante in evidenza',
    none: 'Nessun menu',
    noneHelp: 'Crea il menu principale o quello del piè di pagina.',
    itemsCount: '{{count}} voci',
    unsavedNew: 'Nuovo menu, non ancora salvato',
    keyInfo: 'Chiave tecnica: {{key}}',
    createAction: 'Crea menu',
    keyHelp: 'Nome interno usato dal tema, per esempio main o footer.',
    mainName: 'Menu principale',
    footerName: 'Menu del piè di pagina',
    display: 'Apertura',
    displayList: 'Elenco semplice',
    displayDropdown: 'Tendina',
    displayMega: 'Mega menu a colonne',
    columns: 'Colonne del mega menu',
    addColumn: 'Aggiungi colonna',
    removeColumn: 'Rimuovi la colonna',
    maxColumns: 'Al massimo {{count}} colonne.',
    untitledColumn: 'Colonna senza titolo',
    columnTitle: 'Titolo della colonna ({{locale}})',
    addColumnItem: 'Aggiungi voce alla colonna',
    highlight: 'Voce in evidenza',
    addHighlight: 'Aggiungi una voce in evidenza',
    removeHighlight: 'Rimuovi la voce in evidenza',
    highlightTitle: 'Titolo dell’evidenza ({{locale}})',
    highlightText: 'Testo breve ({{locale}})',
    highlightCta: 'Testo del pulsante ({{locale}})',
    pagesGroup: 'Pagine',
    contentsGroup: 'Contenuti',
    pageNotLive: '{{title}} (non pubblicata)',
    pageMissing: 'Pagina non più disponibile',
    preview: 'Anteprima',
    previewHelp: 'Come lo disegna il sito, anche prima di salvare.',
    previewEmpty: 'Nessuna voce visibile nel sito.',
    previewIncomplete: 'L’anteprima compare quando ogni voce ha un nome e ogni colonna un titolo.',
    noWarnings: 'Tutte le voci portano a una destinazione raggiungibile.',
    warningsTitle: '{{count}} voci non compaiono nel sito',
    markerHelp: 'Per mostrarlo nell’intestazione o nel piè di pagina del tema scrivi {{marker}} nel suo HTML.',
    warning: {
      target_missing: 'la destinazione non esiste più.',
      page_not_published: 'la pagina non è pubblicata.',
      page_trashed: 'la pagina è nel cestino.',
      page_scheduled: 'la pagina è programmata per una data futura.',
    },
  },
  theme: {
    intro: 'Gestisci i token del design system e il CSS specifico del sito, con anteprima prima del salvataggio.',
    themes: 'Temi del sito',
    active: 'Attivo',
    variants: 'varianti',
    newName: 'Nome del nuovo tema',
    create: 'Crea tema',
    derive: 'Deriva da questo tema',
    activate: 'Attiva',
    activated: 'Tema attivato.',
    created: 'Tema derivato e attivato.',
    createdBase: 'Tema creato e attivato.',
    derivedFrom: 'Derivato da',
    original: 'Tema originale',
    token: 'Token',
    value: 'Valore',
    kind: 'Tipo',
    addVariant: 'Nuova variante colore',
    variantAdded: 'Variante aggiunta.',
    additionalCss: 'CSS aggiuntivo',
    cssHelp: 'Viene applicato dopo il CSS generato dai token. Script e HTML non sono accettati.',
    livePreview: 'Anteprima del tema',
    draftPreview: 'Mostra anche le modifiche non salvate',
    previewTitle: 'Una pagina coerente, dal primo token',
    previewText: 'Colori, tipografia, spazi e forme appartengono al sito e vestono tutte le sezioni.',
    previewAction: 'Azione principale',
    saved: 'Tema salvato.',
    partialSaved: 'Ho riletto il tema: le modifiche già accettate restano salvate e quelle ancora da applicare sono conservate nel modulo.',
    partialUnknown: 'Non è stato possibile rileggere il tema: le modifiche nel modulo sono conservate, ma verifica lo stato prima di riprovare.',
    empty: 'Nessun tema disponibile',
    emptyHelp: 'Crea il primo tema per iniziare a definire l’aspetto del sito.',
    mode: {
      light: 'Tema chiaro',
      dark: 'Tema scuro',
      system: 'Come il sistema',
    },
    summary: '{{tokens}} valori · {{variants}} varianti',
    saveFirst: 'Salva o annulla le modifiche prima di passare a un altro tema.',
    derivedFromName: 'Deriva da «{{name}}»',
    tokens: 'Valori del tema',
    pickColor: 'Scegli il colore per {{name}}',
    kinds: {
      color: 'Colore',
      typography: 'Tipografia',
      spacing: 'Spaziatura',
      grid: 'Griglia',
      shape: 'Forma',
      motion: 'Animazione',
      icon: 'Icona',
    },
    variantHelp: 'Varianti di questo tema: {{variants}}',
    unsavedTokens: 'Ci sono valori del tema non salvati.',
  },
  sections: {
    intro: 'La libreria condivisa dal compositore e dal motore di pubblicazione.',
    available: 'disponibili',
    search: 'Cerca una sezione…',
    definition: 'Definizione',
    key: 'Chiave tecnica',
    properties: 'Proprietà',
    variants: 'Varianti',
    generatedHelp: 'Questa definizione genera il pannello proprietà del compositore.',
    readOnly: 'La libreria è fornita dal renderer: qui si consulta, nelle pagine si usa.',
    count: '{{count}} sezioni disponibili',
    noMatch: 'Nessuna sezione corrisponde alla ricerca.',
  },
  pages: {
    title: 'Pagine',
    intro: 'Componi l’albero delle sezioni e controlla il risultato con il renderer reale.',
    pages: 'Pagine del sito',
    sections: 'Sezioni della pagina',
    create: 'Crea pagina',
    created: 'Pagina creata.',
    none: 'Nessuna pagina',
    treeHelp: 'L’ordine qui è l’ordine della pagina pubblicata.',
    addSection: 'Aggiungi sezione',
    empty: 'Nessuna sezione',
    emptyHelp: 'Scegli una definizione dalla libreria e aggiungila alla pagina.',
    properties: 'Proprietà',
    selectSection: 'Seleziona una sezione per modificarne le proprietà.',
    preview: 'Anteprima reale',
    previewSaved: 'Mostra l’ultima versione salvata',
    saved: 'Composizione salvata.',
    noPages: 'Nessuna pagina',
    noPagesHelp: 'Crea la prima pagina componendola a sezioni.',
    address: 'Indirizzo',
    sectionsColumn: 'Sezioni',
    sectionCount: '{{count}} sezioni',
    createTitle: 'Nuova pagina',
    createHelp: 'Scegli l’indirizzo: le sezioni le aggiungi subito dopo.',
    backToList: 'Tutte le pagine',
    openPreview: 'Apri anteprima',
    newSection: 'Aggiungi sezione',
    sectionTechnical: 'Sezione {{key}} · {{id}}',
    moveUp: 'Sposta su',
    moveDown: 'Sposta giù',
    removeSection: 'Rimuovi sezione',
    noProps: 'Questa sezione non ha proprietà da impostare.',
    itemsCount: '{{count}} elementi',
    notSet: 'Non impostato',
    advanced: 'Avanzate',
    advancedLabel: '{{label}} (avanzato)',
    advancedHelp: 'Valore strutturato: modificalo solo se sai cosa stai facendo.',
    untitled: 'Senza titolo',
    status: {
      draft: 'Bozza',
      review: 'In revisione',
      published: 'Pubblicata',
      trashed: 'Nel cestino',
    },
    unpublishedBadge: 'Modifiche non pubblicate',
    unpublishedBadgeHelp: 'La versione pubblicata è diversa da quella su cui stai lavorando: le modifiche escono solo con «Pubblica ora».',
    scheduledChip: 'Esce il {{when}}',
    publishedOn: 'Pubblicata il {{when}}',
    list: {
      search: 'Cerca una pagina',
      searchPlaceholder: 'Cerca per titolo o indirizzo',
      filterStatus: 'Filtra per stato',
      allStatuses: 'Attive',
      view: 'Vista',
      tree: 'Albero',
      flat: 'Elenco',
      title: 'Pagina',
      statusColumn: 'Stato',
      releaseColumn: 'Uscita',
      loading: 'Caricamento delle pagine…',
      errorTitle: 'Non è stato possibile caricare le pagine',
      emptyFiltered: 'Nessuna pagina corrisponde',
      emptyFilteredHelp: 'Cambia la ricerca o il filtro per stato.',
      clearFilters: 'Azzera i filtri',
      actionsFor: 'Azioni per {{title}}',
      openComposer: 'Componi le sezioni',
      trashHelp: 'Le pagine nel cestino non sono sul sito. Ripristinane una per tornare a modificarla.',
      notOnSiteTitle: 'Una pagina nuova non è subito sul sito',
      notOnSite: 'Una pagina nasce come bozza. Va online solo dopo «Pubblica ora» (o alla data programmata) e poi «Pubblica sito»: «Pubblicata» vuol dire pronta, non ancora visibile ai visitatori.',
    },
    newDialog: {
      title: 'Nuova pagina',
      titleLabel: 'Titolo',
      titleHelp: 'Quello che leggono i visitatori e i motori di ricerca. Puoi cambiarlo quando vuoi.',
      slugLabel: 'Ultima parte dell’indirizzo',
      slugHelp: 'Solo lettere minuscole, numeri e trattini: «chi-siamo».',
      parentLabel: 'Pagina genitore',
      parentNone: 'Nessuna: sotto la home del sito',
      willBeAt: 'L’indirizzo sarà {{address}}',
      createdDraft: 'Pagina creata come bozza. Non è ancora sul sito.',
      notLive: 'La pagina nasce come bozza: non è sul sito finché non la pubblichi e poi premi «Pubblica sito».',
      submit: 'Crea e apri la pagina',
      refused: 'La pagina non è stata creata',
    },
    problems: {
      titleMissing: 'Scrivi il titolo della pagina.',
      slugMissing: 'Scrivi l’ultima parte dell’indirizzo.',
      slugInvalid: 'Usa solo lettere minuscole, numeri e singoli trattini, senza spazi (esempio: chi-siamo).',
      metaTitleTooLong: 'Il titolo per i motori di ricerca può avere al massimo 70 caratteri.',
      metaDescriptionTooLong: 'La descrizione può avere al massimo 200 caratteri.',
      scheduleMissing: 'Scegli la data e l’ora di uscita.',
      schedulePast: 'La data di uscita deve essere nel futuro. Per pubblicare subito usa «Pubblica ora».',
    },
    detail: {
      loading: 'Caricamento della pagina…',
      composer: 'Componi le sezioni',
      history: 'Cronologia',
      back: 'Torna al dettaglio della pagina',
      newChild: 'Aggiungi una sottopagina',
      openDetail: 'Apri «{{title}}»',
      openSite: 'Apri sul sito',
      version: 'Versione {{version}}',
      readOnly: 'Hai accesso in sola lettura: puoi guardare la pagina ma non modificarla.',
      reloaded: 'Pagina ricaricata con le modifiche di un’altra persona.',
      trashedBanner: 'Questa pagina è nel cestino: non è sul sito e non si può modificare. Ripristinala per tornare a lavorarci.',
    },
    online: {
      title: 'Sul sito?',
      draft: 'Questa pagina è una bozza e non è sul sito. Per portarla online usa «Pubblica ora» o «Programma l’uscita», poi premi «Pubblica sito».',
      review: 'Questa pagina è in revisione e non è sul sito. Dopo l’approvazione usa «Pubblica ora» o «Programma l’uscita», poi premi «Pubblica sito».',
      published: '«Pubblicata» vuol dire pronta, non ancora online: i visitatori la vedono dopo che premi «Pubblica sito».',
      publishedChanges: 'I visitatori vedono la versione pubblicata il {{when}}, non quella su cui lavori. Le modifiche escono con «Pubblica ora» e poi «Pubblica sito».',
      scheduled: 'Uscita programmata per il {{when}}. Il sito la mostrerà da quella data, ma solo con una «Pubblica sito» fatta dopo.',
      trashed: 'Nel cestino: la prossima volta che pubblichi il sito la pagina sparisce dai visitatori.',
      publishSite: 'Pubblica sito',
    },
    titleCard: {
      title: 'Titolo',
      subtitle: 'Il nome della pagina nel CMS e sul sito.',
      label: 'Titolo della pagina',
      save: 'Salva il titolo',
      saved: 'Titolo salvato.',
    },
    seo: {
      title: 'Come appare nei motori di ricerca',
      subtitle: 'Titolo e descrizione per Google, immagine per le condivisioni, indirizzo canonico e indicizzazione.',
      metaTitle: 'Titolo per i motori di ricerca',
      metaTitleHelp: 'Consigliato fino a 70 caratteri; se è vuoto si usa il titolo della pagina.',
      metaDescription: 'Descrizione',
      metaDescriptionHelp: 'Consigliata fino a 200 caratteri: è il testo sotto il titolo nei risultati.',
      ogImage: 'Immagine per le condivisioni',
      ogImageHelp: 'Indirizzo di un’immagine (https://…). Lascia vuoto per non indicarla.',
      canonical: 'Indirizzo canonico',
      canonicalHelp: 'Solo se questa pagina ripete un’altra: indica quella da considerare originale.',
      noindex: 'Non indicizzare questa pagina',
      noindexHelp: 'I motori di ricerca non la mostreranno nei risultati.',
      counter: '{{count}} / {{max}}',
      save: 'Salva la SEO',
      saved: 'SEO salvata.',
      noChanges: 'Nessuna modifica da salvare.',
    },
    move: {
      title: 'Indirizzo e posizione',
      subtitle: 'Dove sta la pagina nell’albero degli indirizzi.',
      slug: 'Ultima parte dell’indirizzo',
      parent: 'Pagina genitore',
      newAddress: 'Nuovo indirizzo: {{address}}',
      currentAddress: 'Indirizzo attuale: {{address}}',
      warning: 'Spostare la pagina cambia il suo indirizzo. Le pagine che ci stanno sotto la seguono e anche il loro indirizzo cambia. I vecchi indirizzi vengono rediretti (301) al nuovo, ma solo dopo «Pubblica sito».',
      children: 'Seguiranno lo spostamento {{count}} pagine che stanno sotto questa.',
      noChildren: 'Sotto questa pagina non ce ne sono altre.',
      previous: 'Vecchi indirizzi rediretti: {{paths}}',
      submit: 'Sposta la pagina',
      moved: 'Pagina spostata.',
      movedChildren: 'Pagina spostata, con {{count}} pagine sotto di lei.',
      sameAddress: 'L’indirizzo non cambia.',
      trashedLocked: 'Una pagina nel cestino non si sposta: ripristinala prima.',
    },
    lifecycle: {
      title: 'Pubblicazione',
      subtitle: 'Il percorso della pagina: bozza, revisione, pubblicata.',
      current: 'Stato attuale',
      submit: 'Invia in revisione',
      return: 'Rimanda in bozza',
      publish: 'Pubblica ora',
      publishChanges: 'Pubblica le modifiche',
      schedule: 'Programma l’uscita',
      unpublish: 'Ritira dal sito',
      discard: 'Annulla le modifiche e torna alla versione pubblicata',
      trash: 'Sposta nel cestino',
      restore: 'Ripristina',
      scheduleLabel: 'Data e ora di uscita',
      scheduleHelp: 'Nel fuso orario del tuo browser. La pagina esce dalla data scelta, con una «Pubblica sito» fatta dopo.',
      scheduleConfirm: 'Programma',
      noActions: 'Non hai i permessi per cambiare lo stato di questa pagina.',
      done: {
        submit: 'Pagina inviata in revisione.',
        return: 'Pagina rimandata in bozza.',
        publish: 'Pagina pubblicata. Per vederla sul sito premi «Pubblica sito».',
        schedule: 'Uscita programmata. Per vederla sul sito serve una «Pubblica sito» dopo quella data.',
        unpublish: 'Pagina ritirata. Sparisce dal sito alla prossima «Pubblica sito».',
        discard: 'Modifiche annullate: la pagina è tornata alla versione pubblicata.',
        trash: 'Pagina spostata nel cestino.',
        restore: 'Pagina ripristinata come bozza.',
      },
      help: {
        submit: 'Chiede una revisione prima di pubblicare.',
        return: 'La pagina torna una bozza, senza toccare il sito.',
        publish: 'Copia la versione di lavoro nella pubblicata.',
        schedule: 'La pagina esce da una data che scegli tu.',
        unpublish: 'La pagina esce dal sito alla prossima pubblicazione del sito.',
        discard: 'Butta via le modifiche non pubblicate.',
        trash: 'Non si cancella: si può ripristinare.',
        restore: 'La pagina torna una bozza.',
      },
      discardTitle: 'Annullare le modifiche?',
      discardMessage: 'Le modifiche fatte dopo l’ultima pubblicazione (titolo, SEO e sezioni) vengono scartate e la pagina torna alla versione pubblicata il {{when}}. Il passaggio resta nella cronologia, ma il testo scartato non si recupera da qui.',
      trashTitle: 'Spostare la pagina nel cestino?',
      trashMessage: 'La pagina «{{title}}» non sarà più sul sito dalla prossima «Pubblica sito». Puoi ripristinarla dal filtro «Nel cestino».',
      unpublishTitle: 'Ritirare la pagina dal sito?',
      unpublishMessage: 'La pagina «{{title}}» resta nel CMS ma sparisce dal sito alla prossima «Pubblica sito». Chi la cerca troverà un errore di pagina non trovata, a meno che tu non crei un redirect. Attenzione: «Pubblica solo questa pagina» non toglie nulla dal sito, un ritiro esce dal sito solo con la prossima pubblicazione completa («Pubblica sito»).',
    },
    conflict: {
      title: 'Qualcun altro ha scritto su questa pagina',
      message: 'Nel frattempo la pagina è cambiata (ora è alla versione {{actualVersion}}, la tua modifica partiva dalla {{expectedVersion}}). Ricarica per vedere cosa è cambiato, oppure riprova la tua azione sulla versione attuale.',
    },
    history: {
      title: 'Cronologia della pagina',
      subtitle: 'Ogni passaggio della pagina, con chi l’ha fatto e perché.',
      back: 'Torna alla pagina',
    },
    event: {
      created: 'Pagina creata',
      titleChanged: 'Titolo cambiato',
      seoChanged: 'SEO cambiata',
      moved: 'Pagina spostata',
      submitted: 'Inviata in revisione',
      returned: 'Rimandata in bozza',
      published: 'Pubblicata',
      unpublished: 'Ritirata dal sito',
      changesDiscarded: 'Modifiche annullate',
      trashed: 'Spostata nel cestino',
      restored: 'Ripristinata',
      layoutReplaced: 'Sezioni salvate',
      sectionAdded: 'Sezione aggiunta',
      sectionMoved: 'Sezione spostata',
      sectionRemoved: 'Sezione rimossa',
      translationLinked: 'Collegata a una traduzione',
      translationUnlinked: 'Traduzione scollegata',
    },
    composer: {
      detail: 'Dettaglio e pubblicazione',
      savedHint: 'Le sezioni salvate restano nella bozza: per portarle sul sito usa «Pubblica ora» nel dettaglio della pagina e poi «Pubblica sito».',
    },
    deploy: {
      button: 'Pubblica solo questa pagina',
      help: 'Porta online solo questa pagina, senza ripubblicare tutto il sito.',
      title: 'Pubblicare solo questa pagina?',
      message: 'La pagina «{{title}}» viene pubblicata nel CMS, se serve, e va online da sola. Il resto del sito non cambia e le altre pagine in bozza restano dove sono.',
      confirm: 'Pubblica la pagina',
      reason: 'Pubblicazione della sola pagina «{{path}}» da Migliore CMS',
      success: 'La pagina è online',
      successHelp: 'Solo questa pagina è stata aggiornata sul sito.',
      failure: 'La pagina non è stata pubblicata',
      deliveryDisabled: 'La distribuzione di questo sito è disattivata, quindi non si può mandare online nulla. Attivala nelle impostazioni di pubblicazione del sito.',
      baselineMissing: 'Serve prima una pubblicazione completa del sito: la pubblicazione di una sola pagina parte da quella. Premi «Pubblica sito» una volta e poi riprova.',
      publisherDown: 'Il servizio di pubblicazione non è configurato o non risponde. Riprova più tardi; se continua, avvisa chi gestisce il CMS.',
    },
  },
  publications: {
    title: 'Pubblicazioni',
    intro: 'Cronologia dei rilasci statici del sito, con avanzamento ed esito.',
    filter: 'Filtra per esito',
    all: 'Tutte',
    inProgress: 'In corso',
    succeeded: 'Riuscita',
    failed: 'Fallita',
    release: 'Rilascio',
    status: 'Esito',
    progress: 'Avanzamento',
    started: 'Avviata',
    destination: 'Destinazione',
    actions: 'Azioni',
    publishKind: 'Pubblicazione',
    rollbackKind: 'Ripristino',
    rollbackOf: 'Ripristina {{id}}',
    openDeploy: 'Apri deploy',
    empty: 'Nessuna pubblicazione',
    emptyHelp: 'Le pubblicazioni appariranno qui dopo il primo rilascio del sito.',
    phase: {
      planning: 'Preparazione',
      rendering: 'Generazione delle pagine',
      deploying: 'Distribuzione',
      completed: 'Completata',
      failed: 'Interrotta',
    },
  },
  rollback: {
    action: 'Ripristina online',
    title: 'Ripristina il rilascio #{{release}}',
    explanation: 'Il CMS chiede al provider autorizzato di ripristinare esattamente il deploy associato a questo rilascio.',
    onlineWarning: 'Questa operazione cambia la versione online del sito.',
    notContent: 'Contenuti, bozze e dati correnti del CMS non vengono modificati. La base operativa non è un archivio durevole.',
    reasonPlaceholder: 'Perché è necessario tornare a questo rilascio?',
    confirm: 'Conferma ripristino',
    baselineNotice: 'Senza base incrementale',
    fullPublishWarning: 'Dopo il ripristino potrebbe servire una pubblicazione completa invece di quella incrementale.',
    done: 'Versione online ripristinata.',
    failed: 'Il ripristino non è riuscito.',
  },
  provision: {
    short: 'Nuovo sito',
    eyebrow: 'Impianto completo',
    title: 'Crea un nuovo sito',
    intro: 'Crea progetto, sito, tema, tipi, pagine e menu in un’unica operazione atomica. Non pubblica automaticamente.',
    identity: 'Cliente, progetto e dominio',
    tenantName: 'Nome cliente',
    projectName: 'Nome progetto',
    languageBrand: 'Lingue e marchio',
    locales: 'Lingue disponibili',
    localesHelp: 'Codici separati da virgola, per esempio it, en.',
    defaultLocale: 'Lingua predefinita',
    accent: 'Colore di accento',
    font: 'Famiglia tipografica',
    content: 'Contenuti iniziali',
    pagesType: 'Pagine',
    articlesType: 'Articoli',
    initialPages: 'Pagine iniziali',
    addPage: 'Aggiungi pagina',
    pageTitle: 'Titolo',
    slug: 'Percorso',
    homeSlug: 'vuoto = home',
    template: 'Template',
    inMenu: 'Nel menu',
    audit: 'Tracciabilità',
    noPublish: 'L’impianto prepara il sito ma non lo pubblica: la pubblicazione resta un gesto separato.',
    create: 'Crea il sito',
    creating: 'Creazione del sito…',
    created: 'Sito creato. Non è ancora online.',
  },
  knowledge: {
    eyebrow: 'Cliente',
    title: 'Conoscenza aziendale',
    intro: 'Fatti e regole che guidano scrittura, revisione e generazione per questo cliente.',
    services: 'Servizi',
    servicesHelp: 'Un servizio per riga, con il nome usato davvero dal cliente.',
    companyHistory: 'Storia dell’azienda',
    companyHistoryHelp: 'Origini, passaggi decisivi e cambiamenti che spiegano l’azienda di oggi.',
    founderStory: 'Storia del fondatore',
    founderStoryHelp: 'Esperienze, motivazioni e fatti biografici utili alla comunicazione.',
    toneOfVoice: 'Tono di voce',
    toneOfVoiceHelp: 'Come scrivere, quale lessico usare e quale atteggiamento mantenere.',
    proofs: 'Prove',
    proofsHelp: 'Risultati, numeri, riconoscimenti o fatti verificabili. Una prova per riga.',
    anecdotes: 'Aneddoti',
    anecdotesHelp: 'Episodi reali che rendono i contenuti specifici e riconoscibili.',
    avoid: 'Cosa evitare',
    avoidHelp: 'Affermazioni, formule, argomenti o stili che non devono comparire.',
    onePerLine: 'Una voce per riga',
    contextTitle: 'Contesto condiviso',
    contextHelp: 'Questi dati appartengono al cliente e alimentano gli strumenti AI su tutti i suoi siti.',
    tenant: 'Cliente',
    fields: 'Campi valorizzati',
    pending: 'Modifiche aperte',
    other: 'Altra conoscenza',
    otherHelp: 'Percorsi non previsti dal modulo principale. Restano visibili, tipati e modificabili senza perdere struttura.',
    noOther: 'Nessun campo aggiuntivo.',
    addField: 'Aggiungi un campo',
    path: 'Percorso',
    valueType: 'Tipo del valore',
    value: 'Valore',
    pathInvalid: 'Il percorso deve usare nomi separati da punti, senza spazi o indici.',
    pathExists: 'Questo percorso esiste già.',
    removeField: 'Rimuovi campo',
    removeTitle: 'Rimuovere questo campo?',
    removeMessage: 'Il percorso {{path}} verrà rimosso dalla conoscenza corrente. La cronologia conserva il fatto.',
    saved: 'Conoscenza salvata.',
    removed: 'Campo rimosso.',
    partialSaved: 'Ho riletto la conoscenza: le modifiche già accettate restano salvate e quelle ancora da applicare sono conservate.',
    partialUnknown: 'Non è stato possibile rileggere la conoscenza: il modulo conserva le modifiche, ma verifica lo stato prima di riprovare.',
    titleFor: 'Conoscenza di {{client}}',
    coreTitle: 'Chi è il cliente',
    coreHelp: 'Le informazioni che l’assistente AI usa per scrivere e rivedere i contenuti.',
    listType: 'Elenco',
  },
  people: {
    eyebrow: 'Cliente',
    title: 'Persone e progetti',
    intro: 'Assegna alle persone esistenti in Security ruoli precisi sui singoli progetti.',
    forbidden: 'Questa area contiene indirizzi email e assegnazioni: è disponibile solo agli amministratori del cliente.',
    capabilityDisabled: 'Ruoli, grant e inviti si registrano già, ma i permessi per progetto non sono ancora applicati: oggi ogni membro vede tutti i progetti. Prima di accenderli guarda chi perderebbe accesso.',
    capabilityLoading: 'Controllo se la gestione delle persone è disponibile…',
    enforcementOffTitle: 'Permessi per progetto non ancora accesi',
    capabilityUnavailable: 'Non è possibile verificare se la gestione delle persone sia attiva. Per sicurezza l’elenco resta chiuso; riprova riportando questa finestra in primo piano.',
    add: 'Registra persona',
    search: 'Cerca',
    searchPlaceholder: 'Email o subject SSO…',
    status: 'Stato',
    allStatuses: 'Tutti gli stati',
    active: 'Attiva',
    disabled: 'Disabilitata',
    total: '{{total}} persone',
    listLabel: 'Persone del cliente',
    projectsCount: '{{count}} progetti',
    rolesCount: '{{count}} ruoli',
    empty: 'Nessuna persona trovata',
    emptyHelp: 'Cambia i filtri oppure registra una membership per un subject SSO esistente.',
    select: 'Seleziona una persona',
    selectHelp: 'Apri una riga per vedere e modificare gli accessi ai progetti.',
    projects: 'Progetti',
    roles: 'Ruoli sul progetto',
    rolesHelp: 'Il grant vale solo per questo progetto: non passa automaticamente ai progetti figli.',
    noAccess: 'Nessun accesso',
    exactAccess: 'Accesso esatto per progetto',
    noElevation: 'Questi ruoli restringono i permessi già concessi da Security e non possono mai aumentarli.',
    saveBeforeProject: 'Salva o annulla le modifiche prima di cambiare progetto.',
    removeGrant: 'Rimuovi accesso',
    grantSaved: 'Accesso al progetto aggiornato.',
    statusSaved: 'Stato della persona aggiornato.',
    noProjects: 'Nessun progetto disponibile',
    noProjectsHelp: 'Crea prima un progetto, poi assegna qui i ruoli.',
    disable: 'Disabilita nel cliente',
    reactivate: 'Riattiva nel cliente',
    selfStatusBlocked: 'Non puoi disabilitare la tua membership da questa schermata.',
    statusConfirmTitle: 'Cambiare lo stato della persona?',
    statusConfirmMessage: 'La modifica vale solo in questo cliente e non disabilita l’account Security globale.',
    removeConfirmTitle: 'Rimuovere l’accesso al progetto?',
    removeConfirmMessage: 'Tutti i ruoli assegnati su questo progetto verranno revocati. I progetti figli non vengono modificati.',
    registerTitle: 'Aggiungi una persona di Security',
    registerHelp: 'Le persone del CMS vengono solo da Security: qui scegli chi entra in questo cliente, nome ed email restano quelli di Security. Non si creano login, password o inviti.',
    subject: 'Subject SSO',
    email: 'Email',
    register: 'Registra',
    registered: 'Persona registrata nel cliente.',
    conflictTitle: 'La persona è cambiata',
    conflictMessage: 'Un altro amministratore ha aggiornato questa persona. Il server è ora alla versione {{actualVersion}}; la tua modifica è ancora disponibile.',
    role: {
      publisher: 'Publisher',
      editor: 'Editor',
      author: 'Autore',
      reviewer: 'Revisore',
      analyst: 'Analista',
      viewer: 'Lettore',
    },
    roleHelp: {
      publisher: 'Modifica e pubblica.',
      editor: 'Scrive e corregge, senza pubblicare.',
      author: 'Scrive contenuti e media.',
      reviewer: 'Legge per revisionare.',
      analyst: 'Consulta contenuti e SEO.',
      viewer: 'Sola lettura dei contenuti.',
    },
    unavailableTitle: 'Gestione delle persone non disponibile',
    project: 'Progetto',
  },
  repositoryMapping: {
    title: 'File nel repository',
    summary: 'Configurazione avanzata dei percorsi sorgente.',
    warningTitle: 'Solo configurazione',
    warning: 'Definire questi percorsi non attiva la sincronizzazione Git e non pubblica nulla. Sincronizzazione e pubblicazione restano operazioni separate.',
    unavailable: 'Funzione non ancora disponibile in questo ambiente.',
    primaryDirty: 'Salva prima le modifiche principali della risorsa: il mapping usa la stessa versione.',
    saveMappingFirst: 'Salva o annulla prima le modifiche del pannello File nel repository.',
    enabled: 'Configura i file di questa risorsa',
    enabledHelp: 'Disattivando questa opzione, il mapping salvato verrà rimosso.',
    metadataPath: 'Percorso metadati',
    pathPlaceholder: 'Inserisci un percorso esplicito nel repository',
    finalNewline: 'Termina il file con una nuova riga',
    sourceBodies: 'Corpi sorgente',
    addBody: 'Aggiungi corpo',
    repositoryPath: 'Percorso nel repository',
    dataPath: 'Percorso del campo dati',
    format: 'Formato',
    noBodies: 'Nessun corpo sorgente configurato.',
    authoredCss: 'CSS scritto a mano',
    authoredCssHelp: 'Riferisce solo il CSS aggiuntivo scritto dall’autore, mai il foglio compilato.',
    requiredPaths: 'Compila tutti i percorsi abilitati prima di salvare.',
    saved: 'Mapping del repository salvato.',
    conflictTitle: 'Il mapping è cambiato',
    conflictMessage: 'Un’altra modifica ha portato la risorsa alla versione {{actualVersion}}. La tua bozza è conservata.',
    reload: 'Ricarica dal server',
    save: 'Salva la mappatura',
  },
  profile: {
    title: 'Profilo pubblico',
    intro: 'Come il sito si presenta a chi legge: nel nome condiviso (og:site_name), nei titoli delle pagine e in llms.txt.',
    publicName: 'Nome pubblico',
    publicNameHelp: 'Vuoto significa che si usa il nome del sito: {{name}}.',
    description: 'Descrizione',
    descriptionHelp: 'Una frase sul sito: {{count}} di {{max}} caratteri.',
    saved: 'Profilo pubblico aggiornato.',
    notSet: 'Non impostata',
    nameFallback: '{{name}} (nome del sito)',
  },
  listings: {
    title: 'Elenchi',
    intro: 'Le pagine indice paginate del sito, come /blog o /corsi: tipo di contenuto, lingua, indirizzo, testi e impaginazione.',
    list: 'Elenchi del sito',
    new: 'Nuovo elenco',
    empty: 'Nessun elenco configurato',
    emptyHelp: 'Aggiungi il primo elenco per pubblicare la pagina indice paginata di un tipo di contenuto.',
    path: 'Indirizzo',
    pathHelp: 'Percorso assoluto senza barra finale, per esempio /blog o /en/courses.',
    type: 'Tipo di contenuto',
    locale: 'Lingua',
    layout: 'Impaginazione',
    pageSize: 'Elementi per pagina',
    key: 'Chiave',
    keyHelp: 'Identifica l’elenco e non si cambia dopo la creazione, per esempio article:it.',
    pageWord: 'Parola della paginazione',
    pageWordHelp: 'Compare negli indirizzi delle pagine successive: /blog/pagina/2.',
    search: 'Mostra la ricerca',
    actions: 'Azioni',
    layouts: {
      cards: 'Schede',
      courseGrid: 'Griglia dei corsi',
      technologyGroups: 'Gruppi per tecnologia',
    },
    copyTitle: 'Testi',
    copy: {
      title: 'Titolo',
      metaTitle: 'Meta title',
      metaDescription: 'Meta description',
      pageLabel: 'Etichetta di pagina',
      heading: 'Intestazione',
      searchPlaceholder: 'Testo nel campo di ricerca',
      readLabel: 'Etichetta del link di lettura',
    },
    titleHelp: 'Obbligatorio: nomina l’elenco nei menu, nella mappa del sito e nel titolo della pagina.',
    pageLabelHelp: 'Nei titoli delle pagine successive, per esempio «Pagina 2». Vuoto: la parola della paginazione con l’iniziale maiuscola.',
    markupTitle: 'Markup fisso',
    markupIntro: 'Le parti fisse della pagina originale; le schede sono sempre generate dai contenuti. Gli script non sono ammessi: appartengono alla cornice del tema.',
    markup: {
      headerHtml: 'Intestazione (HTML)',
      beforeHtml: 'Prima delle schede (HTML)',
      aroundHtml: 'Attorno alle schede (HTML)',
      afterHtml: 'Dopo le schede (HTML)',
    },
    edit: 'Modifica',
    editing: 'Modifica dell’elenco {{key}}',
    creating: 'Nuovo elenco',
    close: 'Chiudi',
    saved: 'Elenco salvato.',
    removed: 'Elenco rimosso.',
    remove: 'Rimuovi elenco',
    removeTitle: 'Rimuovere l’elenco?',
    removeMessage: 'L’elenco {{path}} lascerà il sito alla prossima pubblicazione completa. Il motivo resta nella cronologia.',
    noTypes: 'Il sito non ha tipi di contenuto abilitati: senza un tipo non si può creare un elenco.',
    problemKey: 'Manca la chiave dell’elenco.',
    problemType: 'Scegli il tipo di contenuto.',
    problemLocale: 'Scegli la lingua.',
    problemPath: 'L’indirizzo deve iniziare con /, per esempio /blog.',
    problemPageSize: 'Gli elementi per pagina sono un numero intero tra 1 e 200.',
    problemTitle: 'Il titolo è obbligatorio.',
    problemKeyTaken: 'Esiste già un elenco con questa chiave: modifica quello invece di crearne un secondo.',
    titleColumn: 'Titolo',
    notFound: 'L’elenco «{{key}}» non esiste più.',
    keyInfo: 'Chiave tecnica: {{key}}',
  },
  frame: {
    title: 'Cornice per lingua',
    intro: 'Head, intestazione, piè di pagina e script che circondano ogni pagina in quella lingua. {{year}} viene sostituito con l’anno al momento della resa.',
    borrowNote: 'Una lingua senza una cornice propria usa quella della lingua predefinita ({{locale}}).',
    borrowNoteNoDefault: 'Una lingua senza una cornice propria usa quella della lingua predefinita del sito.',
    languages: 'Lingue della cornice',
    own: 'propria',
    borrowed: 'presa in prestito',
    fields: {
      headHtml: 'Head (HTML)',
      headerHtml: 'Intestazione (HTML)',
      footerHtml: 'Piè di pagina (HTML)',
      scriptsHtml: 'Script (HTML)',
    },
    reasonLabel: 'Motivo della modifica alla cornice',
    reasonPlaceholder: 'Perché cambi la cornice? Resta nella cronologia.',
    save: 'Salva la cornice',
    saved: 'Cornice di {{locale}} salvata.',
    remove: 'Rimuovi la cornice',
    removeTitle: 'Rimuovere la cornice?',
    removeMessage: 'Dopo la rimozione le pagine in {{locale}} useranno la cornice della lingua predefinita.',
    removed: 'Cornice di {{locale}} rimossa.',
    noLocales: 'Il sito non ha lingue configurate: definiscile nella configurazione del sito.',
  },
  errors: {
    generic: 'Si è verificato un errore imprevisto.',
    network: 'Impossibile contattare l\'API. Controlla la connessione.',
  },
  language: {
    it: 'Italiano',
    en: 'Inglese',
    itNative: 'Italiano',
    enNative: 'English',
  },
  reasonNote: {
    label: 'Nota per lo storico',
    hint: 'Se la lasci vuota, nello storico va: «{{reason}}»',
  },
  pagination: {
    rowsPerPage: 'Righe per pagina',
    range: '{{from}}–{{to}} di {{total}}',
    previous: 'Pagina precedente',
    next: 'Pagina successiva',
  },
  reason: {
    fields: {
      none: 'Modifica da Migliore CMS',
      one: 'Modificato il campo «{{a}}» da Migliore CMS',
      two: 'Modificati i campi «{{a}}» e «{{b}}» da Migliore CMS',
      three: 'Modificati i campi «{{a}}», «{{b}}» e «{{c}}» da Migliore CMS',
      many: 'Modificati {{count}} campi («{{a}}», «{{b}}» e altri) da Migliore CMS',
    },
    status: 'Stato cambiato in «{{status}}» da Migliore CMS',
    restore: 'Contenuto ripristinato dal cestino da Migliore CMS',
    trash: 'Contenuto spostato nel cestino da Migliore CMS',
    create: 'Nuovo contenuto «{{slug}}» creato da Migliore CMS',
    publish: 'Pubblicazione del sito da Migliore CMS',
    rollback: 'Ripristino del rilascio #{{release}} da Migliore CMS',
    provision: 'Nuovo sito «{{name}}» creato da Migliore CMS',
    knowledge: 'Conoscenza aggiornata ({{fields}}) da Migliore CMS',
    knowledgeRemove: 'Rimosso il campo «{{field}}» dalla conoscenza da Migliore CMS',
    grant: 'Accesso di {{email}} al progetto «{{project}}» aggiornato da Migliore CMS',
    grantRemove: 'Accesso di {{email}} al progetto «{{project}}» rimosso da Migliore CMS',
    register: 'Registrata la persona {{email}} da Migliore CMS',
    disable: 'Persona disabilitata nel cliente da Migliore CMS',
    reactivate: 'Persona {{email}} riattivata nel cliente da Migliore CMS',
    listing: 'Elenco «{{path}}» aggiornato da Migliore CMS',
    listingRemove: 'Elenco «{{path}}» rimosso da Migliore CMS',
    menu: 'Menu «{{key}}» aggiornato da Migliore CMS',
    pageCreate: 'Nuova pagina «{{path}}» creata da Migliore CMS',
    page: 'Pagina «{{path}}» aggiornata da Migliore CMS',
    pageTitle: 'Titolo della pagina «{{path}}» cambiato da Migliore CMS',
    pageSeo: 'SEO della pagina «{{path}}» aggiornata da Migliore CMS',
    pageMove: 'Pagina «{{path}}» spostata sotto «{{to}}» da Migliore CMS',
    pageSubmit: 'Pagina «{{path}}» inviata in revisione da Migliore CMS',
    pageReturn: 'Pagina «{{path}}» rimandata in bozza da Migliore CMS',
    pagePublish: 'Pagina «{{path}}» pubblicata da Migliore CMS',
    pageSchedule: 'Pagina «{{path}}» programmata per il {{date}} da Migliore CMS',
    pageUnpublish: 'Pagina «{{path}}» ritirata dal sito da Migliore CMS',
    pageDiscard: 'Modifiche della pagina «{{path}}» annullate da Migliore CMS',
    pageTrash: 'Pagina «{{path}}» spostata nel cestino da Migliore CMS',
    pageRestore: 'Pagina «{{path}}» ripristinata dal cestino da Migliore CMS',
    domain: 'Dominio {{domain}} aggiunto da Migliore CMS',
    repository: 'Repository del sito aggiornato da Migliore CMS',
    repositoryMapping: 'Mappatura del repository aggiornata da Migliore CMS',
    locales: 'Lingue del sito aggiornate ({{locales}}) da Migliore CMS',
    deliveryOn: 'Distribuzione attivata da Migliore CMS',
    deliveryOff: 'Distribuzione disattivata da Migliore CMS',
    profile: 'Profilo del sito aggiornato da Migliore CMS',
    settingsGeneric: 'Impostazioni del sito aggiornate da Migliore CMS',
    frame: 'Cornice HTML ({{locale}}) aggiornata da Migliore CMS',
    frameRemove: 'Cornice HTML ({{locale}}) rimossa da Migliore CMS',
    theme: 'Tema «{{name}}» aggiornato da Migliore CMS',
    themeCreate: 'Nuovo tema «{{name}}» creato da Migliore CMS',
    themeActivate: 'Tema «{{name}}» attivato da Migliore CMS',
    themeVariant: 'Variante «{{name}}» creata da Migliore CMS',
    aiWrite: 'Campo «{{field}}» scritto con l’assistente AI da Migliore CMS',
    aiReview: 'Campo «{{field}}» rivisto con l’assistente AI da Migliore CMS',
  },
  siteStatus: {
    draft: 'In preparazione',
    live: 'Online',
    paused: 'In pausa',
  },
  shell: {
    skipToContent: 'Vai al contenuto',
    expandMenu: 'Espandi il menu',
    collapseMenu: 'Riduci il menu',
    home: 'Tutti i siti',
    publishHelp: 'Porta online tutte le modifiche salvate di questo sito',
    account: 'Account',
    accountOf: 'Account di {{name}}',
    navigation: 'Navigazione principale',
  },
  search: {
    open: 'Cerca',
    close: 'Chiudi la ricerca',
    placeholder: 'Cerca in tutti i contenuti',
    clear: 'Cancella la ricerca',
    results: 'Risultati della ricerca',
    searching: 'Ricerca in corso…',
    noResults: 'Nessun contenuto trovato per «{{query}}»',
    seeAllCount: 'Vedi tutti i {{total}} risultati',
    seeAll: 'Vedi tutti i risultati',
    inSite: 'in {{site}}',
  },
  switcher: {
    noSite: 'Scegli un sito',
    titleFor: 'Siti di {{client}}',
    title: 'Scegli un sito',
    allSites: 'Tutti i siti',
    search: 'Cerca un sito',
    searchPlaceholder: 'Nome del sito o dominio',
    noMatch: 'Nessun sito corrisponde alla ricerca.',
    noSites: 'Non ci sono ancora siti. Creane uno per iniziare.',
    otherProject: 'Altri siti',
  },
  live: {
    openSite: 'Apri il sito',
    openOnSite: 'Apri sul sito',
    notPublishedYet: 'Non ancora online: salva e pubblica il sito per vederlo',
    noAddress: 'Questo contenuto non ha ancora un indirizzo pubblico.',
  },
  notFound: {
    title: 'Pagina non trovata',
    help: 'L’indirizzo non corrisponde a nessuna pagina del CMS. Controlla il link o torna ai tuoi siti.',
  },
  overview: {
    online: 'Online',
    release: 'Rilascio #{{release}} · {{when}}',
    allPublications: 'Tutte le pubblicazioni',
    publishHintTitle: 'Salvare non basta per andare online',
    publishHint: 'Le modifiche salvate restano nel CMS finché non premi «Pubblica sito»: solo allora il sito pubblico si aggiorna.',
    manage: 'Gestisci il sito',
    pagesHelp: 'Pagine composte a sezioni',
    menusHelp: 'Navigazione principale e piè di pagina',
    listingsHelp: 'Pagine indice di articoli e corsi',
    themeHelp: 'Colori, caratteri e cornice HTML',
    sectionsHelp: 'I blocchi disponibili per le pagine',
    settingsHelp: 'Domini, lingue e repository',
    noContentHelp: 'Crea il primo contenuto di questo sito.',
  },
  home: {
    greeting: 'Ciao {{name}}',
    subtitle: 'Scegli un sito oppure riprendi da dove avevi lasciato.',
    recent: 'Modificati di recente',
    recentHelp: 'Gli ultimi contenuti aggiornati in tutti i siti.',
    noRecent: 'Nessun contenuto modificato di recente.',
    sites: 'I tuoi siti',
    noSitesHelp: 'Non hai ancora accesso a nessun sito. Creane uno o chiedi l’accesso a un amministratore.',
  },
  ...libraryIt,
  ...layoutsIt,
  ...polishIt,
  ...editorIt,
  ...mainImageIt,
  ...contentTreeIt,
  ...shellIt,
  ...sectionTreeIt,
  ...editorialIt,
  ...aiGenIt,
  ...inPlaceIt,
  ...accessIt,
  ...themeDesignIt,
}

export const en: DictNode = {
  app: {
    title: 'CMS',
  },
  auth: {
    title: 'Sign in to the CMS',
    description: 'The content brain of Sviluppatore Migliore.',
    submit: 'Continue with Security',
    checking: 'Checking the session…',
    invalidTitle: 'Sign-in failed',
    networkTitle: 'Could not reach the API',
    logout: 'Sign out',
    signedInAs: 'Signed in with single sign-on',
    errors: {
      generic: 'The sign-in did not go through. Try again; if it keeps happening, tell whoever administers the system.',
      login_required: 'There is no Security session any more: sign in again.',
      session_expired: 'That took too long: a sign-in request expires after ten minutes. Start again.',
      state_mismatch: 'The answer does not match the sign-in request that started here, so it was refused. Try again from this page.',
      issuer_mismatch: 'The answer came from an address other than the single sign-on one, so it was refused.',
      access_denied: 'Access was denied on Security.',
      sso_not_configured: 'Single sign-on is not configured in this environment yet. Whoever administers the system has to do it.',
      sso_unavailable: 'Security is not answering right now. Try again shortly.',
      sso_token_exchange_failed: 'Security did not accept the final exchange of the sign-in. Try again; if it persists, tell whoever administers the system.',
      interaction_required: 'Security needs you to confirm: press the button and carry on there.',
      consent_required: 'The first time, Security asks for consent for the CMS: press the button and say yes on its page.',
      account_selection_required: 'Security wants to know which account you are using: press the button and choose.',
      unauthorized: 'Security answered with an identity the CMS cannot accept: either your account has no client, or the token is not valid. Tell whoever administers the system.',
      tenant_not_provisioned: 'Your Security organization is not bound to a CMS customer yet: until that binding exists there is nothing to show. Tell whoever administers the system.',
      exchange_failed: 'The final exchange with Security did not work. Try again; if it persists, tell whoever administers the system.',
      missing_code: 'Security came back without the sign-in code. Try again.',
      missing_id_token: 'Security did not return the identity along with the token. Tell whoever administers the system.',
      jwks_unavailable: 'The signing keys of Security cannot be read right now. Try again shortly.',
      code: 'Technical code: {{code}}',
    },
    returnHint: 'After signing in you go back to the page you were opening.',
    footnote: 'Sign-in goes through Security, the single sign-on of Sviluppatore Migliore.',
  },
  nav: {
    overview: 'Overview',
    contents: 'Contents',
    knowledge: 'Knowledge',
    people: 'People',
    apiKeys: 'API keys',
    operator: 'Operator',
    home: 'All sites',
    pages: 'Pages',
    menus: 'Menus',
    listings: 'Listings',
    media: 'Media',
    schedule: 'Calendar',
    redirects: 'Redirects',
    theme: 'Theme',
    sections: 'Sections',
    publications: 'Publications',
    settings: 'Settings',
  },
  workspace: {
    site: 'Active site',
    work: 'Work',
    system: 'System',
    live: 'Published site',
    notLive: 'Not published yet',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    toggleTheme: 'Change theme',
    language: 'Interface language',
    user: 'User',
  },
  dashboard: {
    title: 'Overview',
    subtitle: 'Editorial status and recent work for the selected site.',
    overview: 'Content status',
    activeContent: 'Active content',
    activeExplanation: 'drafts, reviews and published content',
    openAll: 'Open all',
    recent: 'Recently changed',
    recentHelp: 'The latest content returned by the CMS for this site.',
    error: 'Could not load the overview',
    noSite: 'No site available',
  },
  status: {
    draft: 'Draft',
    review: 'In review',
    published: 'Published',
    trashed: 'Trashed',
    draftPlural: 'Drafts',
    reviewPlural: 'In review',
    publishedPlural: 'Published',
  },
  list: {
    title: 'Contents',
    subtitle: 'Search, filter and open content for the selected site.',
    resultCount: 'contents found',
    filters: {
      site: 'Site',
      allSites: 'All sites',
      type: 'Type',
      allTypes: 'All types',
      status: 'Status',
      allStatuses: 'All statuses',
    },
    search: {
      placeholder: 'Search by title…',
      label: 'Search by title',
    },
    columns: {
      title: 'Title',
      type: 'Type',
      status: 'Status',
      updatedAt: 'Updated',
      author: 'Author',
      authorUnavailable: 'Not available: the content list does not yet carry who made the last change. See that content\'s history.',
      locale: 'Language',
    },
    empty: 'No content matches these filters.',
    loading: 'Loading…',
    errorTitle: 'Could not load the contents',
    filtersErrorTitle: 'Sites and content types are unavailable',
    newContent: 'New content',
    openHistory: 'History',
    publishSite: 'Publish site',
    pagination: {
      previous: 'Previous',
      next: 'Next',
      pageInfo: '{{from}}–{{to}} of {{total}}',
    },
    subtitleFor: 'All content of {{site}}',
    actions: {
      open: 'Open',
      label: 'Actions',
      for: 'Actions for “{{title}}”',
    },
    emptyTitle: 'No content',
    emptyFiltered: 'No content matches the filters',
    emptyFilteredHelp: 'Try removing some filters or changing the search.',
    clearFilters: 'Clear filters',
    emptyHelp: 'Create the first content of this site.',
  },
  edit: {
    titleFor: 'Editing: {{title}}',
    newTitle: 'New content',
    reasonLabel: 'Reason for the change',
    reasonPlaceholder: 'Why are you making this change? It stays in the history.',
    reasonRequired: 'The reason is required: it ends up in the history and makes everyone’s work legible.',
    requiredFields: 'Choose a site, type and locale; enter the path and reason for the change.',
    save: 'Save',
    saving: 'Saving…',
    saved: 'Saved',
    unsaved: 'You have unsaved changes. Save them before changing status, using AI, or trashing the content.',
    unsavedShort: 'Unsaved changes',
    noChanges: 'No changes',
    details: 'Details',
    versionLabel: 'Version',
    workflow: 'Editorial status',
    contentHelp: 'Editorial fields and structured content data.',
    seoHelp: 'Metadata, search-engine data and generative-answer fields.',
    noSeoFields: 'This type does not declare separate SEO fields.',
    leaveTitle: 'Unsaved changes',
    leaveMessage: 'Leaving this page will discard your unsaved changes.',
    leaveConfirm: 'Leave without saving',
    tabs: {
      content: 'Content',
      seo: 'SEO',
      tools: 'Tools',
    },
    cancel: 'Cancel',
    slug: 'Path (slug)',
    locale: 'Locale',
    version: 'Version {{version}}',
    otherFieldsTitle: 'Other',
    otherFieldsHelp: 'Fields present in the content but not declared by the type. They stay editable: nothing is ever lost.',
    protectedField: 'Protected field: cannot be edited here.',
    protectedFieldReason: 'Protected fields only change through their dedicated operation (for instance, changing the slug).',
    unknownFieldType: 'Field type "{{type}}" is not yet supported: editable as raw JSON.',
    trash: 'Trash',
    restore: 'Restore',
    changeStatus: 'Change status',
    confirmTrash: 'Move this content to trash?',
    loadError: 'Could not load this content.',
    backToList: 'Back to the list',
    savedPublishedHint: 'Saved as published. Press “Publish site” to put it online.',
    readOnlyRole: 'Your role only allows reading this content.',
    unknownType: 'The content type “{{type}}” is no longer defined: fields are shown in technical form.',
    inTrash: 'This content is in the trash',
    inTrashHelp: 'It does not appear on the site. Restore it to edit it again.',
    savedNotOnlineTitle: 'Saved, but not online yet',
    savedNotOnline: 'The changes are in the CMS. The public site updates when you press “Publish site”.',
    tabsLabel: 'Editor sections',
    noDeclaredFields: 'This content type declares no editable fields.',
    searchPreview: 'Search result preview',
    noDescription: 'No description: search engines will pick one from the text.',
    statusPending: 'On save the status becomes “{{status}}”',
    statusHelp: {
      draft: 'Draft: visible only in the CMS.',
      review: 'In review: ready to be checked.',
      published: 'Published: goes online with the next site publication.',
      trashed: 'In the trash: not shown on the site.',
    },
    address: 'Address',
    publicAddress: 'Public address',
    type: 'Type',
    publishedAt: 'Published on',
    writtenWith: 'Written with the assistant',
    reviewedWith: 'Reviewed with the assistant',
    saveBar: 'Save bar',
    discard: 'Discard changes',
    saveAndMarkPublished: 'Save and mark as published',
    trashed: 'Content moved to the trash.',
    restored: 'Content restored.',
    cannotCreate: 'Your role does not allow creating content on this site.',
    newSubtitle: 'Choose the type, a title and an address: you complete the rest in the editor.',
    titleLabel: 'Title',
    titleHelp: 'Used to suggest the address. You will write it in the content fields.',
    titleNotStored: 'The title is not saved on its own: fill it in the fields after creating.',
    addressPreview: 'Address: {{address}}',
    slugHelp: 'Lowercase letters, numbers and hyphens only.',
    creating: 'Creating…',
    create: 'Create content',
    technicalPath: 'Technical path: {{path}}',
  },
  page: {
    titleFor: 'On the page: {{title}}',
    openForm: 'Open in the form',
    openInPage: 'Edit on the page',
    frameTitle: 'The page, editable where you read it',
    loadingPage: 'Loading the page…',
    howItWorks: 'This is the real page, rendered by the CMS. The orange outlines are the fields: click one and type. There are {{fields}} of them on this page. Changes are saved below, with a reason.',
    readOnly: 'You can read this page but not change it: your role has no write permission.',
    pending: 'Unsaved changes: {{count}}.',
    noPending: 'No unsaved changes.',
    discard: 'Discard the changes',
    linkBlocked: 'The link to {{href}} is not followed from here: it would take away the page you are editing, unsaved changes and all.',
    preview: 'Preview',
    eyebrow: 'Page composition',
  },
  fields: {
    string: 'Text',
    text: 'Long text',
    richtext: 'Rich text',
    number: 'Number',
    boolean: 'Boolean',
    enum: 'Closed list',
    date: 'Date',
    media: 'Media',
    reference: 'Reference',
    list: 'List of objects',
    json: 'JSON',
    addItem: 'Add item',
    removeItem: 'Remove',
    moveUp: 'Move up',
    moveDown: 'Move down',
    jsonInvalid: 'Invalid JSON: the change was not applied.',
    referenceHelp: 'Reference to a content of type "{{to}}". Paste its id.',
    booleanTrue: 'Yes',
    booleanFalse: 'No',
    richtextHelp: 'Formatted text in Markdown.',
    mediaHelp: 'Address of the image or file.',
    jsonHelp: 'Structured value in JSON format.',
    itemNumber: 'Item {{number}}',
  },
  conflict: {
    title: 'Someone else changed this content',
    description: 'The document changed in the meantime (it is now at version {{actualVersion}}, your edit started from {{expectedVersion}}). What you wrote was not lost: choose what to keep.',
    mine: 'Your version',
    theirs: 'The server\'s version',
    keepMine: 'Keep mine and retry',
    keepTheirs: 'Reload the server\'s',
    dismiss: 'Dismiss',
  },
  history: {
    title: 'Content history',
    who: 'Who',
    when: 'When',
    ip: 'IP address',
    reason: 'Reason',
    event: 'Event',
    viewAt: 'View at this version',
    viewingAt: 'You are viewing the document as it was at version {{version}}.',
    backToCurrent: 'Back to current version',
    back: 'Back to content',
    empty: 'No events recorded.',
    loading: 'Loading history…',
    errorTitle: 'Could not load the history',
    subtitle: 'Every change, who made it and why.',
    versionNumber: 'Version {{version}}',
    person: 'Person',
  },
  publish: {
    title: 'Publication',
    confirmMessage: 'Publish the site now? Published content becomes publicly visible.',
    confirm: 'Publish',
    cancel: 'Cancel',
    inProgress: 'Publishing…',
    progress: '{{completed}} of {{total}} pages prepared',
    phase: {
      planning: 'Checking content to publish…',
      rendering: 'Generating static pages…',
      deploying: 'Deploying to the Cloudflare network…',
      completed: 'Deployment completed.',
      failed: 'Publication stopped.',
    },
    success: 'Site published.',
    openDeployment: 'Open deployed site',
    failure: 'The publication failed.',
    warnings: 'Warnings from this publication: {{count}}',
    checkStatus: 'Check status',
    confirmMessageFor: 'Every saved change of {{site}} goes online. It takes about a minute.',
    successHelp: 'The public site is up to date.',
  },
  ai: {
    title: 'AI writing and review',
    description: 'Choose the subscription account and field explicitly. The CMS uses this customer’s company knowledge and records which account worked on it.',
    write: 'Writing',
    review: 'Review',
    chooseAccount: 'Choose a subscription',
    chooseField: 'Choose the field',
    prompt: 'Describe what to write or review…',
    generate: 'Start',
    working: 'Working…',
    purpose: 'What do you want to do',
    account: 'AI account',
    field: 'Field',
    promptLabel: 'Instructions for the assistant',
    promptHelp: 'Describe what you want. The client knowledge is added automatically.',
    done: 'Text inserted in the field: check it and save.',
  },
  release: {
    notes: 'Release notes',
    updated: 'Updated to version {{version}}',
    dismiss: 'Got it',
    notesHelp: 'What is new in Migliore CMS, newest first.',
  },
  common: {
    add: 'Add',
    cancel: 'Cancel',
    loading: 'Loading…',
    remove: 'Remove',
    save: 'Save',
    saved: 'Saved',
    saving: 'Saving…',
    search: 'Search',
    unsaved: 'Unsaved changes',
    close: 'Close',
    more: 'More actions',
  },
  guard: {
    title: 'Unsaved changes',
    message: 'Leaving will discard your unsaved changes.',
    leave: 'Leave without saving',
    stay: 'Stay here',
  },
  site: {
    area: 'Site',
    navigation: 'Site management',
    settings: 'Configuration',
    settingsIntro: 'Identity, domains, languages and technical destination for the selected site.',
    menu: 'Menu',
    theme: 'Theme',
    sections: 'Sections',
    identity: 'Site identity',
    name: 'Name',
    primaryDomain: 'Primary domain',
    status: 'Status',
    project: 'Project',
    noTheme: 'No active theme',
    locales: 'Languages',
    domains: 'Domains',
    primary: 'Primary',
    alias: 'Alias',
    addDomain: 'Connect a domain',
    domainIntro: 'Add an alias or change the primary domain.',
    domain: 'Domain',
    makePrimary: 'Set as primary domain',
    domainSaved: 'Domain updated.',
    repository: 'Publishing repository',
    repositoryIntro: 'Repository and isolated branch used for the site assets and publication.',
    repositoryUrl: 'Repository URL',
    branch: 'CMS branch',
    repositorySaved: 'Repository updated.',
    languages: 'Site languages',
    languagesIntro: 'Define the available languages and the default. No language is assumed.',
    defaultLocale: 'Default language',
    localesInvalid: 'The default language must be included in the list.',
    localesSaved: 'Languages updated.',
    delivery: 'Publishing destination',
    deliveryIntro: 'Enable the Cloudflare Pages destination prepared by the platform operator.',
    deliveryProvider: 'Provider',
    deliveryProject: 'Authorized project',
    deliveryAuthorization: 'Authorization',
    deliveryAuthorized: 'Authorized',
    deliveryUnauthorized: 'Missing',
    deliveryNotBound: 'Not assigned',
    deliveryToggle: 'Enable publications to this destination',
    deliveryOperatorRequired: 'The destination is not authorized yet. Ask the platform operator to bind this site: the project name and account cannot be chosen here.',
    deliveryEnabled: 'Publishing destination enabled.',
    deliveryDisabled: 'Publishing destination disabled.',
    lastPublished: 'Published {{when}}',
    neverPublished: 'Never published',
    notFound: 'Site not found',
    notFoundHelp: 'The site does not exist or you are not allowed to see it.',
    readOnly: 'You can view these settings but not change them.',
    addDomainAction: 'Add domain',
    openTheme: 'Open theme',
    publications: 'Publications',
    publicationCount: '{{count}} publications',
  },
  menu: {
    intro: 'Build ordered, nested navigation linked to real content.',
    menus: 'Site menus',
    items: 'items',
    new: 'New menu',
    key: 'Key',
    addItem: 'Add item',
    label: 'Label',
    targetType: 'Target',
    reference: 'Content',
    external: 'External address',
    query: 'Dynamic collection',
    content: 'Linked content',
    url: 'Address',
    contentType: 'Content type',
    limit: 'Limit',
    visibility: 'Visibility',
    always: 'Always',
    style: 'Style',
    normal: 'Normal',
    featured: 'Featured',
    addChild: 'Add child',
    moveUp: 'Move up',
    moveDown: 'Move down',
    empty: 'The menu is empty',
    emptyHelp: 'Add the first item and choose a real target.',
    saved: 'Menu saved.',
    untitled: 'Untitled item',
    labelIn: 'Label ({{locale}})',
    desktop: 'Desktop',
    mobile: 'Mobile',
    callToAction: 'Highlighted button',
    none: 'No menus',
    noneHelp: 'Create the main menu or the footer menu.',
    itemsCount: '{{count}} items',
    unsavedNew: 'New menu, not saved yet',
    keyInfo: 'Technical key: {{key}}',
    createAction: 'Create menu',
    keyHelp: 'Internal name used by the theme, for example main or footer.',
    mainName: 'Main menu',
    footerName: 'Footer menu',
    display: 'Opening',
    displayList: 'Plain list',
    displayDropdown: 'Dropdown',
    displayMega: 'Mega menu with columns',
    columns: 'Mega menu columns',
    addColumn: 'Add column',
    removeColumn: 'Remove column',
    maxColumns: 'At most {{count}} columns.',
    untitledColumn: 'Untitled column',
    columnTitle: 'Column title ({{locale}})',
    addColumnItem: 'Add item to the column',
    highlight: 'Highlighted item',
    addHighlight: 'Add a highlighted item',
    removeHighlight: 'Remove the highlighted item',
    highlightTitle: 'Highlight title ({{locale}})',
    highlightText: 'Short text ({{locale}})',
    highlightCta: 'Button text ({{locale}})',
    pagesGroup: 'Pages',
    contentsGroup: 'Contents',
    pageNotLive: '{{title}} (not published)',
    pageMissing: 'Page no longer available',
    preview: 'Preview',
    previewHelp: 'How the site draws it, even before saving.',
    previewEmpty: 'No entry is visible on the site.',
    previewIncomplete: 'The preview appears once every entry has a name and every column a title.',
    noWarnings: 'Every entry leads to a reachable destination.',
    warningsTitle: '{{count}} entries do not appear on the site',
    markerHelp: 'To show it in the theme header or footer, write {{marker}} in its HTML.',
    warning: {
      target_missing: 'the destination no longer exists.',
      page_not_published: 'the page is not published.',
      page_trashed: 'the page is in the trash.',
      page_scheduled: 'the page is scheduled for a future date.',
    },
  },
  theme: {
    intro: 'Manage design-system tokens and site-specific CSS, with a preview before saving.',
    themes: 'Site themes',
    active: 'Active',
    variants: 'variants',
    newName: 'New theme name',
    create: 'Create theme',
    derive: 'Derive from this theme',
    activate: 'Activate',
    activated: 'Theme activated.',
    created: 'Theme derived and activated.',
    createdBase: 'Theme created and activated.',
    derivedFrom: 'Derived from',
    original: 'Original theme',
    token: 'Token',
    value: 'Value',
    kind: 'Type',
    addVariant: 'New colour variant',
    variantAdded: 'Variant added.',
    additionalCss: 'Additional CSS',
    cssHelp: 'Applied after token-generated CSS. Scripts and HTML are rejected.',
    livePreview: 'Theme preview',
    draftPreview: 'Also shows unsaved changes',
    previewTitle: 'A coherent page, from the first token',
    previewText: 'Colours, type, spacing and shapes belong to the site and dress every section.',
    previewAction: 'Primary action',
    saved: 'Theme saved.',
    partialSaved: 'I reloaded the theme: accepted changes remain saved and pending changes are preserved in the form.',
    partialUnknown: 'The theme could not be reloaded: form changes are preserved, but verify the state before retrying.',
    empty: 'No theme available',
    emptyHelp: 'Create the first theme to start defining this site’s appearance.',
    mode: {
      light: 'Light theme',
      dark: 'Dark theme',
      system: 'Match system',
    },
    summary: '{{tokens}} values · {{variants}} variants',
    saveFirst: 'Save or discard your changes before switching theme.',
    derivedFromName: 'Derived from “{{name}}”',
    tokens: 'Theme values',
    pickColor: 'Pick the colour for {{name}}',
    kinds: {
      color: 'Colour',
      typography: 'Typography',
      spacing: 'Spacing',
      grid: 'Grid',
      shape: 'Shape',
      motion: 'Motion',
      icon: 'Icon',
    },
    variantHelp: 'Variants of this theme: {{variants}}',
    unsavedTokens: 'Some theme values are not saved.',
  },
  sections: {
    intro: 'The library shared by the composer and publishing renderer.',
    available: 'available',
    search: 'Search sections…',
    definition: 'Definition',
    key: 'Technical key',
    properties: 'Properties',
    variants: 'Variants',
    generatedHelp: 'This definition generates the composer property panel.',
    readOnly: 'The renderer provides this library: inspect it here and use it in pages.',
    count: '{{count}} sections available',
    noMatch: 'No section matches your search.',
  },
  pages: {
    title: 'Pages',
    intro: 'Compose the section tree and check the result using the real renderer.',
    pages: 'Site pages',
    sections: 'Page sections',
    create: 'Create page',
    created: 'Page created.',
    none: 'No page',
    treeHelp: 'The order here is the order of the published page.',
    addSection: 'Add section',
    empty: 'No sections',
    emptyHelp: 'Choose a definition from the library and add it to the page.',
    properties: 'Properties',
    selectSection: 'Select a section to edit its properties.',
    preview: 'Real preview',
    previewSaved: 'Shows the latest saved version',
    saved: 'Composition saved.',
    noPages: 'No pages',
    noPagesHelp: 'Create the first page by composing sections.',
    address: 'Address',
    sectionsColumn: 'Sections',
    sectionCount: '{{count}} sections',
    createTitle: 'New page',
    createHelp: 'Choose the address: you add the sections right after.',
    backToList: 'All pages',
    openPreview: 'Open preview',
    newSection: 'Add section',
    sectionTechnical: 'Section {{key}} · {{id}}',
    moveUp: 'Move up',
    moveDown: 'Move down',
    removeSection: 'Remove section',
    noProps: 'This section has no properties to set.',
    itemsCount: '{{count}} items',
    notSet: 'Not set',
    advanced: 'Advanced',
    advancedLabel: '{{label}} (advanced)',
    advancedHelp: 'Structured value: change it only if you know what you are doing.',
    untitled: 'Untitled',
    status: {
      draft: 'Draft',
      review: 'In review',
      published: 'Published',
      trashed: 'In the trash',
    },
    unpublishedBadge: 'Unpublished changes',
    unpublishedBadgeHelp: 'The published version differs from the one you are working on: the changes only go out with “Publish now”.',
    scheduledChip: 'Goes out on {{when}}',
    publishedOn: 'Published on {{when}}',
    list: {
      search: 'Search a page',
      searchPlaceholder: 'Search by title or address',
      filterStatus: 'Filter by status',
      allStatuses: 'Active',
      view: 'View',
      tree: 'Tree',
      flat: 'List',
      title: 'Page',
      statusColumn: 'Status',
      releaseColumn: 'Release',
      loading: 'Loading pages…',
      errorTitle: 'The pages could not be loaded',
      emptyFiltered: 'No page matches',
      emptyFilteredHelp: 'Change the search or the status filter.',
      clearFilters: 'Clear filters',
      actionsFor: 'Actions for {{title}}',
      openComposer: 'Compose the sections',
      trashHelp: 'Pages in the trash are not on the site. Restore one to edit it again.',
      notOnSiteTitle: 'A new page is not on the site right away',
      notOnSite: 'A page starts as a draft. It goes online only after “Publish now” (or on the scheduled date) and then “Publish site”: “Published” means ready, not yet visible to visitors.',
    },
    newDialog: {
      title: 'New page',
      titleLabel: 'Title',
      titleHelp: 'What visitors and search engines read. You can change it any time.',
      slugLabel: 'Last part of the address',
      slugHelp: 'Lowercase letters, numbers and hyphens only: “about-us”.',
      parentLabel: 'Parent page',
      parentNone: 'None: directly under the site home',
      willBeAt: 'The address will be {{address}}',
      createdDraft: 'Page created as a draft. It is not on the site yet.',
      notLive: 'The page starts as a draft: it is not on the site until you publish it and then press “Publish site”.',
      submit: 'Create and open the page',
      refused: 'The page was not created',
    },
    problems: {
      titleMissing: 'Write the page title.',
      slugMissing: 'Write the last part of the address.',
      slugInvalid: 'Use lowercase letters, numbers and single hyphens only, no spaces (example: about-us).',
      metaTitleTooLong: 'The search engine title can have at most 70 characters.',
      metaDescriptionTooLong: 'The description can have at most 200 characters.',
      scheduleMissing: 'Choose the release date and time.',
      schedulePast: 'The release date must be in the future. To publish right away use “Publish now”.',
    },
    detail: {
      loading: 'Loading the page…',
      composer: 'Compose the sections',
      history: 'History',
      back: 'Back to the page detail',
      newChild: 'Add a subpage',
      openDetail: 'Open “{{title}}”',
      openSite: 'Open on the site',
      version: 'Version {{version}}',
      readOnly: 'You have read-only access: you can look at the page but not change it.',
      reloaded: 'Page reloaded with another person’s changes.',
      trashedBanner: 'This page is in the trash: it is not on the site and cannot be edited. Restore it to work on it again.',
    },
    online: {
      title: 'On the site?',
      draft: 'This page is a draft and is not on the site. To bring it online use “Publish now” or “Schedule release”, then press “Publish site”.',
      review: 'This page is in review and is not on the site. After approval use “Publish now” or “Schedule release”, then press “Publish site”.',
      published: '“Published” means ready, not yet online: visitors see it after you press “Publish site”.',
      publishedChanges: 'Visitors see the version published on {{when}}, not the one you are working on. The changes go out with “Publish now” and then “Publish site”.',
      scheduled: 'Release scheduled for {{when}}. The site will show it from that date, but only with a “Publish site” done after it.',
      trashed: 'In the trash: the next time you publish the site the page disappears for visitors.',
      publishSite: 'Publish site',
    },
    titleCard: {
      title: 'Title',
      subtitle: 'The page name in the CMS and on the site.',
      label: 'Page title',
      save: 'Save the title',
      saved: 'Title saved.',
    },
    seo: {
      title: 'How it looks in search engines',
      subtitle: 'Title and description for Google, image for shares, canonical address and indexing.',
      metaTitle: 'Search engine title',
      metaTitleHelp: 'Up to 70 characters recommended; if empty the page title is used.',
      metaDescription: 'Description',
      metaDescriptionHelp: 'Up to 200 characters recommended: it is the text under the title in the results.',
      ogImage: 'Image for shares',
      ogImageHelp: 'Address of an image (https://…). Leave empty to give none.',
      canonical: 'Canonical address',
      canonicalHelp: 'Only if this page repeats another one: name the one to treat as the original.',
      noindex: 'Do not index this page',
      noindexHelp: 'Search engines will not show it in the results.',
      counter: '{{count}} / {{max}}',
      save: 'Save the SEO',
      saved: 'SEO saved.',
      noChanges: 'No changes to save.',
    },
    move: {
      title: 'Address and position',
      subtitle: 'Where the page sits in the address tree.',
      slug: 'Last part of the address',
      parent: 'Parent page',
      newAddress: 'New address: {{address}}',
      currentAddress: 'Current address: {{address}}',
      warning: 'Moving the page changes its address. The pages under it follow and their address changes too. The old addresses are redirected (301) to the new one, but only after “Publish site”.',
      children: '{{count}} pages under this one will follow the move.',
      noChildren: 'There are no other pages under this one.',
      previous: 'Old addresses redirected: {{paths}}',
      submit: 'Move the page',
      moved: 'Page moved.',
      movedChildren: 'Page moved, with {{count}} pages under it.',
      sameAddress: 'The address does not change.',
      trashedLocked: 'A page in the trash cannot be moved: restore it first.',
    },
    lifecycle: {
      title: 'Publishing',
      subtitle: 'The page path: draft, review, published.',
      current: 'Current status',
      submit: 'Send to review',
      return: 'Send back to draft',
      publish: 'Publish now',
      publishChanges: 'Publish the changes',
      schedule: 'Schedule release',
      unpublish: 'Take off the site',
      discard: 'Discard the changes and go back to the published version',
      trash: 'Move to the trash',
      restore: 'Restore',
      scheduleLabel: 'Release date and time',
      scheduleHelp: 'In your browser’s time zone. The page goes out from the chosen date, with a “Publish site” done after.',
      scheduleConfirm: 'Schedule',
      noActions: 'You do not have permission to change the status of this page.',
      done: {
        submit: 'Page sent to review.',
        return: 'Page sent back to draft.',
        publish: 'Page published. To see it on the site press “Publish site”.',
        schedule: 'Release scheduled. To see it on the site a “Publish site” is needed after that date.',
        unpublish: 'Page taken down. It disappears from the site at the next “Publish site”.',
        discard: 'Changes discarded: the page is back to the published version.',
        trash: 'Page moved to the trash.',
        restore: 'Page restored as a draft.',
      },
      help: {
        submit: 'Asks for a review before publishing.',
        return: 'The page becomes a draft again, without touching the site.',
        publish: 'Copies the working version into the published one.',
        schedule: 'The page goes out on a date you choose.',
        unpublish: 'The page leaves the site at the next site publication.',
        discard: 'Throws away the unpublished changes.',
        trash: 'Nothing is deleted: it can be restored.',
        restore: 'The page becomes a draft again.',
      },
      discardTitle: 'Discard the changes?',
      discardMessage: 'The changes made after the last publication (title, SEO and sections) are thrown away and the page goes back to the version published on {{when}}. The step stays in the history, but the discarded text cannot be recovered from here.',
      trashTitle: 'Move the page to the trash?',
      trashMessage: 'The page “{{title}}” will no longer be on the site from the next “Publish site”. You can restore it from the “In the trash” filter.',
      unpublishTitle: 'Take the page off the site?',
      unpublishMessage: 'The page “{{title}}” stays in the CMS but disappears from the site at the next “Publish site”. Anyone looking for it will get a page-not-found error, unless you create a redirect. Note: “Publish only this page” removes nothing from the site; a take-down leaves the site only with the next full publication (“Publish site”).',
    },
    conflict: {
      title: 'Someone else wrote on this page',
      message: 'The page changed in the meantime (it is now at version {{actualVersion}}, your change started from {{expectedVersion}}). Reload to see what changed, or retry your action on the current version.',
    },
    history: {
      title: 'Page history',
      subtitle: 'Every step of the page, with who did it and why.',
      back: 'Back to the page',
    },
    event: {
      created: 'Page created',
      titleChanged: 'Title changed',
      seoChanged: 'SEO changed',
      moved: 'Page moved',
      submitted: 'Sent to review',
      returned: 'Sent back to draft',
      published: 'Published',
      unpublished: 'Taken off the site',
      changesDiscarded: 'Changes discarded',
      trashed: 'Moved to the trash',
      restored: 'Restored',
      layoutReplaced: 'Sections saved',
      sectionAdded: 'Section added',
      sectionMoved: 'Section moved',
      sectionRemoved: 'Section removed',
      translationLinked: 'Linked to a translation',
      translationUnlinked: 'Translation unlinked',
    },
    composer: {
      detail: 'Detail and publishing',
      savedHint: 'Saved sections stay in the draft: to put them on the site use “Publish now” in the page detail and then “Publish site”.',
    },
    deploy: {
      button: 'Publish only this page',
      help: 'Puts only this page online, without republishing the whole site.',
      title: 'Publish only this page?',
      message: 'The page “{{title}}” is published in the CMS, if needed, and goes online on its own. The rest of the site does not change and other draft pages stay where they are.',
      confirm: 'Publish the page',
      reason: 'Publication of the single page “{{path}}” from Migliore CMS',
      success: 'The page is online',
      successHelp: 'Only this page was updated on the site.',
      failure: 'The page was not published',
      deliveryDisabled: 'Delivery for this site is turned off, so nothing can go online. Turn it on in the site publishing settings.',
      baselineMissing: 'A full site publication is needed first: publishing a single page starts from that. Press “Publish site” once and then try again.',
      publisherDown: 'The publishing service is not configured or not answering. Try again later; if it persists, tell whoever runs the CMS.',
    },
  },
  publications: {
    title: 'Publications',
    intro: 'History of the site’s static releases, with progress and outcome.',
    filter: 'Filter by outcome',
    all: 'All',
    inProgress: 'In progress',
    succeeded: 'Succeeded',
    failed: 'Failed',
    release: 'Release',
    status: 'Outcome',
    progress: 'Progress',
    started: 'Started',
    destination: 'Destination',
    actions: 'Actions',
    publishKind: 'Publication',
    rollbackKind: 'Rollback',
    rollbackOf: 'Restores {{id}}',
    openDeploy: 'Open deploy',
    empty: 'No publications',
    emptyHelp: 'Publications will appear here after the site’s first release.',
    phase: {
      planning: 'Planning',
      rendering: 'Rendering pages',
      deploying: 'Deploying',
      completed: 'Completed',
      failed: 'Stopped',
    },
  },
  rollback: {
    action: 'Restore online',
    title: 'Restore release #{{release}}',
    explanation: 'The CMS asks the authorized provider to restore the exact deployment associated with this release.',
    onlineWarning: 'This operation changes the online version of the site.',
    notContent: 'Current CMS content, drafts and data are not changed. The operational baseline is not a durable archive.',
    reasonPlaceholder: 'Why is it necessary to return to this release?',
    confirm: 'Confirm rollback',
    baselineNotice: 'No incremental baseline',
    fullPublishWarning: 'A full publication may be needed after the rollback instead of an incremental one.',
    done: 'Online version restored.',
    failed: 'Rollback failed.',
  },
  provision: {
    short: 'New site',
    eyebrow: 'Complete setup',
    title: 'Create a new site',
    intro: 'Create project, site, theme, types, pages and menu in one atomic operation. It does not publish automatically.',
    identity: 'Customer, project and domain',
    tenantName: 'Customer name',
    projectName: 'Project name',
    languageBrand: 'Languages and brand',
    locales: 'Available languages',
    localesHelp: 'Comma-separated codes, for example it, en.',
    defaultLocale: 'Default language',
    accent: 'Accent colour',
    font: 'Font family',
    content: 'Initial content',
    pagesType: 'Pages',
    articlesType: 'Articles',
    initialPages: 'Initial pages',
    addPage: 'Add page',
    pageTitle: 'Title',
    slug: 'Path',
    homeSlug: 'empty = home',
    template: 'Template',
    inMenu: 'In menu',
    audit: 'Audit trail',
    noPublish: 'Setup prepares the site but does not publish it: publication stays a separate action.',
    create: 'Create site',
    creating: 'Creating site…',
    created: 'Site created. It is not online yet.',
  },
  knowledge: {
    eyebrow: 'Customer',
    title: 'Company knowledge',
    intro: 'Facts and rules that guide writing, review and generation for this customer.',
    services: 'Services',
    servicesHelp: 'One service per line, using the customer’s real name for it.',
    companyHistory: 'Company history',
    companyHistoryHelp: 'Origins, turning points and changes that explain today’s company.',
    founderStory: 'Founder story',
    founderStoryHelp: 'Experience, motivations and biographical facts useful for communication.',
    toneOfVoice: 'Tone of voice',
    toneOfVoiceHelp: 'How to write, which vocabulary to use and which attitude to maintain.',
    proofs: 'Proof',
    proofsHelp: 'Results, numbers, awards or verifiable facts. One proof per line.',
    anecdotes: 'Anecdotes',
    anecdotesHelp: 'Real episodes that make content specific and recognizable.',
    avoid: 'What to avoid',
    avoidHelp: 'Claims, phrases, topics or styles that must not appear.',
    onePerLine: 'One item per line',
    contextTitle: 'Shared context',
    contextHelp: 'This data belongs to the customer and feeds AI tools across all their sites.',
    tenant: 'Customer',
    fields: 'Populated fields',
    pending: 'Pending changes',
    other: 'Other knowledge',
    otherHelp: 'Paths outside the main form. They remain visible, typed and editable without losing structure.',
    noOther: 'No additional fields.',
    addField: 'Add a field',
    path: 'Path',
    valueType: 'Value type',
    value: 'Value',
    pathInvalid: 'The path must use dot-separated names, without spaces or indexes.',
    pathExists: 'This path already exists.',
    removeField: 'Remove field',
    removeTitle: 'Remove this field?',
    removeMessage: 'The {{path}} path will be removed from current knowledge. History keeps the fact.',
    saved: 'Knowledge saved.',
    removed: 'Field removed.',
    partialSaved: 'I reloaded knowledge: accepted changes remain saved and pending changes are preserved.',
    partialUnknown: 'Knowledge could not be reloaded: the form preserves changes, but verify the state before retrying.',
    titleFor: 'Knowledge of {{client}}',
    coreTitle: 'Who the client is',
    coreHelp: 'The information the AI assistant uses to write and review content.',
    listType: 'List',
  },
  people: {
    eyebrow: 'Client',
    title: 'People and projects',
    intro: 'Assign existing Security identities precise roles on individual projects.',
    forbidden: 'This area contains email addresses and assignments, so it is available only to client administrators.',
    capabilityDisabled: 'Roles, grants and invitations are already recorded, but project permissions are not enforced yet: today every member sees every project. Before turning them on, check who would lose access.',
    capabilityLoading: 'Checking whether people management is available…',
    enforcementOffTitle: 'Project permissions not turned on yet',
    capabilityUnavailable: 'The app cannot verify whether people management is active. The directory stays closed for safety; bring this window into focus to try again.',
    add: 'Register person',
    search: 'Search',
    searchPlaceholder: 'Email or SSO subject…',
    status: 'Status',
    allStatuses: 'All statuses',
    active: 'Active',
    disabled: 'Disabled',
    total: '{{total}} people',
    listLabel: 'Client people',
    projectsCount: '{{count}} projects',
    rolesCount: '{{count}} roles',
    empty: 'No people found',
    emptyHelp: 'Change the filters or register a membership for an existing SSO subject.',
    select: 'Select a person',
    selectHelp: 'Open a row to view and change project access.',
    projects: 'Projects',
    roles: 'Project roles',
    rolesHelp: 'The grant applies only to this project and does not automatically pass to child projects.',
    noAccess: 'No access',
    exactAccess: 'Exact project access',
    noElevation: 'These roles restrict permissions already granted by Security and can never increase them.',
    saveBeforeProject: 'Save or discard the changes before switching project.',
    removeGrant: 'Remove access',
    grantSaved: 'Project access updated.',
    statusSaved: 'Person status updated.',
    noProjects: 'No projects available',
    noProjectsHelp: 'Create a project first, then assign roles here.',
    disable: 'Disable in client',
    reactivate: 'Reactivate in client',
    selfStatusBlocked: 'You cannot disable your own membership from this screen.',
    statusConfirmTitle: 'Change this person’s status?',
    statusConfirmMessage: 'This change applies only in this client and does not disable the global Security account.',
    removeConfirmTitle: 'Remove project access?',
    removeConfirmMessage: 'All roles assigned on this project will be revoked. Child projects are not changed.',
    registerTitle: 'Add a person from Security',
    registerHelp: 'CMS people come only from Security: here you choose who joins this client, and name and email stay those on Security. No login, password or invitation is created.',
    subject: 'SSO subject',
    email: 'Email',
    register: 'Register',
    registered: 'Person registered in the client.',
    conflictTitle: 'The person changed',
    conflictMessage: 'Another administrator updated this person. The server is now at version {{actualVersion}}; your change is still available.',
    role: {
      publisher: 'Publisher',
      editor: 'Editor',
      author: 'Author',
      reviewer: 'Reviewer',
      analyst: 'Analyst',
      viewer: 'Viewer',
    },
    roleHelp: {
      publisher: 'Edits and publishes.',
      editor: 'Writes and corrects without publishing.',
      author: 'Writes content and media.',
      reviewer: 'Reads to review.',
      analyst: 'Reads content and SEO.',
      viewer: 'Content read-only.',
    },
    unavailableTitle: 'People management unavailable',
    project: 'Project',
  },
  repositoryMapping: {
    title: 'Repository files',
    summary: 'Advanced source-path configuration.',
    warningTitle: 'Configuration only',
    warning: 'Defining these paths does not enable Git synchronization and does not publish anything. Synchronization and publication remain separate operations.',
    unavailable: 'This feature is not available in this environment yet.',
    primaryDirty: 'Save the resource’s main changes first: the mapping uses the same version.',
    saveMappingFirst: 'Save or discard the Repository files panel changes first.',
    enabled: 'Configure files for this resource',
    enabledHelp: 'Turning this off removes the saved mapping when you save.',
    metadataPath: 'Metadata path',
    pathPlaceholder: 'Enter an explicit repository path',
    finalNewline: 'End the file with a newline',
    sourceBodies: 'Source bodies',
    addBody: 'Add body',
    repositoryPath: 'Repository path',
    dataPath: 'Data field path',
    format: 'Format',
    noBodies: 'No source bodies configured.',
    authoredCss: 'Authored CSS',
    authoredCssHelp: 'References only author-written additional CSS, never the compiled stylesheet.',
    requiredPaths: 'Fill every enabled path before saving.',
    saved: 'Repository mapping saved.',
    conflictTitle: 'The mapping changed',
    conflictMessage: 'Another change moved the resource to version {{actualVersion}}. Your draft is preserved.',
    reload: 'Reload from server',
    save: 'Save mapping',
  },
  profile: {
    title: 'Public profile',
    intro: 'How the site presents itself to readers: in the shared name (og:site_name), in page titles and in llms.txt.',
    publicName: 'Public name',
    publicNameHelp: 'Empty means the site name is used: {{name}}.',
    description: 'Description',
    descriptionHelp: 'One sentence about the site: {{count}} of {{max}} characters.',
    saved: 'Public profile updated.',
    notSet: 'Not set',
    nameFallback: '{{name}} (site name)',
  },
  listings: {
    title: 'Listings',
    intro: 'The paginated index pages of the site, such as /blog or /courses: content type, language, address, copy and layout.',
    list: 'Site listings',
    new: 'New listing',
    empty: 'No listing configured',
    emptyHelp: 'Add the first listing to publish the paginated index page of a content type.',
    path: 'Address',
    pathHelp: 'Absolute path without the trailing slash, for example /blog or /en/courses.',
    type: 'Content type',
    locale: 'Language',
    layout: 'Layout',
    pageSize: 'Items per page',
    key: 'Key',
    keyHelp: 'Identifies the listing and cannot change after creation, for example article:en.',
    pageWord: 'Pagination word',
    pageWordHelp: 'Appears in the addresses of later pages: /en/blog/page/2.',
    search: 'Show the search',
    actions: 'Actions',
    layouts: {
      cards: 'Cards',
      courseGrid: 'Course grid',
      technologyGroups: 'Technology groups',
    },
    copyTitle: 'Copy',
    copy: {
      title: 'Title',
      metaTitle: 'Meta title',
      metaDescription: 'Meta description',
      pageLabel: 'Page label',
      heading: 'Heading',
      searchPlaceholder: 'Search field placeholder',
      readLabel: 'Read link label',
    },
    titleHelp: 'Required: it names the listing in menus, in the sitemap and in the page title.',
    pageLabelHelp: 'In the titles of later pages, for example “Page 2”. Empty: the pagination word, capitalised.',
    markupTitle: 'Fixed markup',
    markupIntro: 'The fixed parts of the original page; the cards are always generated from content. Scripts are not accepted: they belong to the theme frame.',
    markup: {
      headerHtml: 'Header (HTML)',
      beforeHtml: 'Before the cards (HTML)',
      aroundHtml: 'Around the cards (HTML)',
      afterHtml: 'After the cards (HTML)',
    },
    edit: 'Edit',
    editing: 'Editing listing {{key}}',
    creating: 'New listing',
    close: 'Close',
    saved: 'Listing saved.',
    removed: 'Listing removed.',
    remove: 'Remove listing',
    removeTitle: 'Remove the listing?',
    removeMessage: 'The listing {{path}} will leave the site at the next full publication. The reason stays in the history.',
    noTypes: 'The site has no enabled content types: a listing cannot be created without one.',
    problemKey: 'The listing key is missing.',
    problemType: 'Choose the content type.',
    problemLocale: 'Choose the language.',
    problemPath: 'The address must start with /, for example /blog.',
    problemPageSize: 'Items per page is a whole number between 1 and 200.',
    problemTitle: 'The title is required.',
    problemKeyTaken: 'A listing with this key already exists: edit that one instead of creating a second.',
    titleColumn: 'Title',
    notFound: 'The listing “{{key}}” no longer exists.',
    keyInfo: 'Technical key: {{key}}',
  },
  frame: {
    title: 'Frame per language',
    intro: 'Head, header, footer and scripts around every page in that language. {{year}} is replaced with the year at render time.',
    borrowNote: 'A language without its own frame uses the default language one ({{locale}}).',
    borrowNoteNoDefault: 'A language without its own frame uses the one of the site default language.',
    languages: 'Frame languages',
    own: 'own',
    borrowed: 'borrowed',
    fields: {
      headHtml: 'Head (HTML)',
      headerHtml: 'Header (HTML)',
      footerHtml: 'Footer (HTML)',
      scriptsHtml: 'Scripts (HTML)',
    },
    reasonLabel: 'Reason for the frame change',
    reasonPlaceholder: 'Why are you changing the frame? It stays in the history.',
    save: 'Save the frame',
    saved: 'Frame for {{locale}} saved.',
    remove: 'Remove the frame',
    removeTitle: 'Remove the frame?',
    removeMessage: 'After removal, pages in {{locale}} will use the default language frame.',
    removed: 'Frame for {{locale}} removed.',
    noLocales: 'The site has no languages configured: define them in the site configuration.',
  },
  errors: {
    generic: 'An unexpected error occurred.',
    network: 'Could not reach the API. Check your connection.',
  },
  language: {
    it: 'Italian',
    en: 'English',
    itNative: 'Italiano',
    enNative: 'English',
  },
  reasonNote: {
    label: 'Note for the history',
    hint: 'If you leave it empty, the history records: “{{reason}}”',
  },
  pagination: {
    rowsPerPage: 'Rows per page',
    range: '{{from}}–{{to}} of {{total}}',
    previous: 'Previous page',
    next: 'Next page',
  },
  reason: {
    fields: {
      none: 'Edited from Migliore CMS',
      one: 'Changed “{{a}}” from Migliore CMS',
      two: 'Changed “{{a}}” and “{{b}}” from Migliore CMS',
      three: 'Changed “{{a}}”, “{{b}}” and “{{c}}” from Migliore CMS',
      many: 'Changed {{count}} fields (“{{a}}”, “{{b}}” and more) from Migliore CMS',
    },
    status: 'Status changed to “{{status}}” from Migliore CMS',
    restore: 'Content restored from the trash from Migliore CMS',
    trash: 'Content moved to the trash from Migliore CMS',
    create: 'New content “{{slug}}” created from Migliore CMS',
    publish: 'Site published from Migliore CMS',
    rollback: 'Release #{{release}} restored from Migliore CMS',
    provision: 'New site “{{name}}” created from Migliore CMS',
    knowledge: 'Knowledge updated ({{fields}}) from Migliore CMS',
    knowledgeRemove: 'Removed “{{field}}” from the knowledge from Migliore CMS',
    grant: 'Access of {{email}} to project “{{project}}” updated from Migliore CMS',
    grantRemove: 'Access of {{email}} to project “{{project}}” removed from Migliore CMS',
    register: 'Registered {{email}} from Migliore CMS',
    disable: 'Person disabled in the client from Migliore CMS',
    reactivate: 'Person {{email}} reactivated in the client from Migliore CMS',
    listing: 'Listing “{{path}}” updated from Migliore CMS',
    listingRemove: 'Listing “{{path}}” removed from Migliore CMS',
    menu: 'Menu “{{key}}” updated from Migliore CMS',
    pageCreate: 'New page “{{path}}” created from Migliore CMS',
    page: 'Page “{{path}}” updated from Migliore CMS',
    pageTitle: 'Title of page “{{path}}” changed from Migliore CMS',
    pageSeo: 'SEO of page “{{path}}” updated from Migliore CMS',
    pageMove: 'Page “{{path}}” moved under “{{to}}” from Migliore CMS',
    pageSubmit: 'Page “{{path}}” sent to review from Migliore CMS',
    pageReturn: 'Page “{{path}}” sent back to draft from Migliore CMS',
    pagePublish: 'Page “{{path}}” published from Migliore CMS',
    pageSchedule: 'Page “{{path}}” scheduled for {{date}} from Migliore CMS',
    pageUnpublish: 'Page “{{path}}” taken off the site from Migliore CMS',
    pageDiscard: 'Changes to page “{{path}}” discarded from Migliore CMS',
    pageTrash: 'Page “{{path}}” moved to the trash from Migliore CMS',
    pageRestore: 'Page “{{path}}” restored from the trash from Migliore CMS',
    domain: 'Domain {{domain}} added from Migliore CMS',
    repository: 'Site repository updated from Migliore CMS',
    repositoryMapping: 'Repository mapping updated from Migliore CMS',
    locales: 'Site languages updated ({{locales}}) from Migliore CMS',
    deliveryOn: 'Delivery enabled from Migliore CMS',
    deliveryOff: 'Delivery disabled from Migliore CMS',
    profile: 'Site profile updated from Migliore CMS',
    settingsGeneric: 'Site settings updated from Migliore CMS',
    frame: 'HTML frame ({{locale}}) updated from Migliore CMS',
    frameRemove: 'HTML frame ({{locale}}) removed from Migliore CMS',
    theme: 'Theme “{{name}}” updated from Migliore CMS',
    themeCreate: 'New theme “{{name}}” created from Migliore CMS',
    themeActivate: 'Theme “{{name}}” activated from Migliore CMS',
    themeVariant: 'Variant “{{name}}” created from Migliore CMS',
    aiWrite: 'Field “{{field}}” written with the AI assistant from Migliore CMS',
    aiReview: 'Field “{{field}}” reviewed with the AI assistant from Migliore CMS',
  },
  siteStatus: {
    draft: 'In preparation',
    live: 'Live',
    paused: 'Paused',
  },
  shell: {
    skipToContent: 'Skip to content',
    expandMenu: 'Expand menu',
    collapseMenu: 'Collapse menu',
    home: 'All sites',
    publishHelp: 'Put every saved change of this site online',
    account: 'Account',
    accountOf: 'Account of {{name}}',
    navigation: 'Main navigation',
  },
  search: {
    open: 'Search',
    close: 'Close search',
    placeholder: 'Search all content',
    clear: 'Clear search',
    results: 'Search results',
    searching: 'Searching…',
    noResults: 'No content found for “{{query}}”',
    seeAllCount: 'See all {{total}} results',
    seeAll: 'See all results',
    inSite: 'in {{site}}',
  },
  switcher: {
    noSite: 'Choose a site',
    titleFor: 'Sites of {{client}}',
    title: 'Choose a site',
    allSites: 'All sites',
    search: 'Search a site',
    searchPlaceholder: 'Site name or domain',
    noMatch: 'No site matches your search.',
    noSites: 'There are no sites yet. Create one to get started.',
    otherProject: 'Other sites',
  },
  live: {
    openSite: 'Open site',
    openOnSite: 'Open on site',
    notPublishedYet: 'Not online yet: save and publish the site to see it',
    noAddress: 'This content has no public address yet.',
  },
  notFound: {
    title: 'Page not found',
    help: 'This address does not match any CMS page. Check the link or go back to your sites.',
  },
  overview: {
    online: 'Online',
    release: 'Release #{{release}} · {{when}}',
    allPublications: 'All publications',
    publishHintTitle: 'Saving is not enough to go online',
    publishHint: 'Saved changes stay in the CMS until you press “Publish site”: only then does the public site update.',
    manage: 'Manage the site',
    pagesHelp: 'Pages built from sections',
    menusHelp: 'Main navigation and footer',
    listingsHelp: 'Index pages for articles and courses',
    themeHelp: 'Colours, fonts and HTML frame',
    sectionsHelp: 'The blocks available to pages',
    settingsHelp: 'Domains, languages and repository',
    noContentHelp: 'Create the first content of this site.',
  },
  home: {
    greeting: 'Hi {{name}}',
    subtitle: 'Choose a site or pick up where you left off.',
    recent: 'Recently edited',
    recentHelp: 'The latest content updated across all sites.',
    noRecent: 'No recently edited content.',
    sites: 'Your sites',
    noSitesHelp: 'You do not have access to any site yet. Create one or ask an administrator for access.',
  },
  ...libraryEn,
  ...layoutsEn,
  ...polishEn,
  ...editorEn,
  ...mainImageEn,
  ...contentTreeEn,
  ...shellEn,
  ...sectionTreeEn,
  ...editorialEn,
  ...aiGenEn,
  ...inPlaceEn,
  ...accessEn,
  ...themeDesignEn,
}

export type Locale = 'it' | 'en'

export const dictionaries: Record<Locale, DictNode> = { it, en }
